/**
 * BokBo Search Results — Full-Page Shopify Search Replacement
 *
 * Loads on /search pages. Reads the `q` URL parameter, calls the BokBo
 * search API, and replaces the native Shopify results container with a
 * responsive product grid.
 *
 * Installation:
 *   Add this script to your Shopify theme (before </body>):
 *   <script src="https://cdn.bokbo.com/widget/bokbo-results.js"></script>
 */

(function () {
  'use strict';

  // ── Anti-flash: hide the native Shopify results container synchronously ─
  // This <style> is injected *before* DOMContentLoaded so the browser never
  // paints the native results. It's removed once we decide what to do:
  //   - searchEnabled=true  → init() replaces the native container, then we
  //                           drop the hide so the BokBo container is visible.
  //   - searchEnabled=false → we drop the hide and let native results render.
  //
  // CRITICAL: only inject on actual search pages. This script is loaded on
  // every page of the storefront (homepage, collections, product pages), so
  // a broad injection would hide non-search layouts until the 3s safety
  // timeout kicked in. We also keep the selector list narrow — specifically
  // search-scoped IDs/classes only, never generic layout wrappers like
  // #MainContent or main[role="main"].
  //
  // Note: the canonical /pages/bokbo-search template is already a blank
  // page (the section contains only an empty #bokbo-search-page-shell
  // div), so there's no native content to hide there — we skip the
  // anti-flash on that route and keep the CSS injection as a fallback
  // only for the legacy /search path and /pages/search-results.
  //
  // The Shopify page handle IS `bokbo-search`, bound via
  // templates/page.bokbo-search.json → sections/bokbo-search-page.liquid.
  // Keep this URL in lockstep with the published page handle — renaming
  // the Shopify page without updating this detection lands shoppers on
  // a 404 or drops them into the legacy anti-flash path.
  var ANTI_FLASH_STYLE_ID = 'bokbo-anti-flash';
  function isBokboSearchPage() {
    var p = window.location.pathname;
    return p === '/pages/bokbo-search' || p === '/pages/bokbo-search/';
  }
  function isWishlistPage() {
    var p = window.location.pathname;
    return p === '/pages/bokbo-wishlist' || p === '/pages/bokbo-wishlist/';
  }
  function isLegacySearchPage() {
    var p = window.location.pathname;
    if (p === '/search' || p === '/search/') return true;
    if (p === '/pages/search-results' || p === '/pages/search-results/') return true;
    return false;
  }
  function isSearchPage() {
    return isBokboSearchPage() || isLegacySearchPage();
  }
  var ANTI_FLASH_SELECTORS = [
    '#shopify-section-template--search',
    '.template-search .search-results',
    '.template-search #search-results',
    '.search-results',
    '#search-results',
    '[data-section-type="search"]',
    '.search-template-section'
  ];
  (function injectAntiFlash() {
    // The dedicated BokBo page is blank by design — nothing to hide — so
    // we skip the CSS injection entirely there. Keep the anti-flash only
    // for the legacy /search and /pages/search-results routes where
    // native Shopify results would otherwise paint before we replace them.
    if (!isLegacySearchPage()) return;
    if (document.getElementById(ANTI_FLASH_STYLE_ID)) return;
    // Narrow to search-specific containers only. Does NOT include generic
    // layout selectors (#MainContent, main#main, .main-content, etc.) — those
    // would hide the whole page on non-search routes if this script ever runs
    // outside isSearchPage(), and they're too blunt even on /search.
    // Hide native search containers AND breadcrumbs synchronously so neither
    // flashes before BokBo takes over. Breadcrumb lives outside the container
    // BokBo replaces, so it must be hidden independently.
    var css = ANTI_FLASH_SELECTORS.join(',') + '{visibility:hidden!important;}' +
      'nav.breadcrumbs,.breadcrumbs__list,ol.breadcrumbs__list,nav[aria-label="Breadcrumb navigation"]{display:none!important;}';
    var style = document.createElement('style');
    style.id = ANTI_FLASH_STYLE_ID;
    style.type = 'text/css';
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
    // Diagnostic: at inject time, the DOM isn't fully parsed yet so most of
    // these queries will likely return null. We re-run the scan once the DOM
    // is ready so we can see which selectors actually exist on this theme.
    function scanSelectors(tag) {
      try {
        var matches = [];
        ANTI_FLASH_SELECTORS.forEach(function (sel) {
          var el = document.querySelector(sel);
          if (el) matches.push(sel);
        });
        console.log('[BokBo anti-flash] ' + tag + ' matching selectors:', matches.length ? matches : '(none)');
      } catch (_) {}
    }
    scanSelectors('inject-time');
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { scanSelectors('DOMContentLoaded'); });
    }
  })();
  function removeAntiFlash() {
    var el = document.getElementById(ANTI_FLASH_STYLE_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  // ── Resolve the current store's myshopify handle ──────────────────
  // Shopify exposes `Shopify.shop` on every storefront page as the full
  // myshopify domain (e.g. "1abf9b-6f.myshopify.com"). We strip the suffix
  // so the widget and the admin dashboard write/read the same Redis key.
  // Falls back to "rackhunt" for standalone/demo pages.
  // Resolve the store identifier used for per-store scoping on the
  // backend (analytics, settings, search namespace).
  //
  // Source of truth is `window.Shopify.shop` — Shopify injects this
  // on every storefront page as "<handle>.myshopify.com" where the
  // handle is the canonical store identifier the backend uses to
  // key Qdrant / Redis. We explicitly do NOT read
  // window.BOKBO_CONFIG.storeId even when the theme sets it: stale
  // theme-config values (e.g. a legacy `storeId: 'rackhunt'` left
  // over from a vanity-domain setup) would silently redirect the
  // widget to the wrong backend namespace, and we prefer breaking
  // visibly on an unrecognized store over quietly searching the
  // wrong one. The Shopify global is correct by construction.
  //
  // Fallback is the compile-time "rackhunt" literal for standalone
  // / dev pages where Shopify global isn't present.
  function resolveStoreId() {
    try {
      if (window.Shopify && typeof window.Shopify.shop === 'string' && window.Shopify.shop) {
        return window.Shopify.shop.replace(/\.myshopify\.com$/i, '').toLowerCase();
      }
    } catch (e) {}
    return 'rackhunt';
  }

  // ── Config ──────────────────────────────────────────────────────
  //
  // Theme-level override: if window.BOKBO_CONFIG.apiUrl is set before
  // this script runs, use it as the API base. The merchant's theme
  // may want to point BokBo at a specific backend (e.g. the Railway
  // deployment domain vs. the vanity domain). We accept both shapes:
  //   - 'https://bokbo.com'         → we append '/search'
  //   - 'https://bokbo.com/search'  → used verbatim
  // Fallback is the vanity domain. This was previously hard-coded and
  // silently ignored any theme-level override, which made
  // "widget posts to bokbo.com while theme expects Railway" a hard bug
  // to spot from console logs alone — the XHR would get CORS-rejected
  // at bokbo.com without ever reaching the diagnostic callbacks.
  function resolveApiUrl() {
    var cfg = (typeof window !== 'undefined' && window.BOKBO_CONFIG) ? window.BOKBO_CONFIG : null;
    var raw = cfg && typeof cfg.apiUrl === 'string' && cfg.apiUrl ? cfg.apiUrl : 'https://bokbo.com';
    // Strip trailing slashes then append /search if the override didn't
    // already include it. Preserves the historical BOKBO.apiUrl contract
    // (callers and wishlist helpers assume the value ends with /search,
    // see WISHLIST_SYNC_URL / getSettingsUrl which strip /search to get
    // the base).
    raw = raw.replace(/\/+$/, '');
    if (!/\/search$/.test(raw)) raw += '/search';
    return raw;
  }
  var BOKBO = {
    apiUrl: resolveApiUrl(),
    storeId: resolveStoreId(),
    accentColor: '#D4390A',
    resultLimit: 200,      // fetch up to 200 from API
    perPage: 40,           // show 40 results per page
    searchEnabled: true,
    infiniteScroll: true,
    skeletonLoading: true,
    // Native BokBo wishlist toggle — mirrors DEFAULT_STORE_SETTINGS on
    // the server side. Default on; the admin dashboard flips it via
    // applySettings below.
    wishlistEnabled: true,
  };
  // Surface the resolved API URL in the console at boot so the
  // "widget posts to X" log in searchApi can be cross-checked against
  // what the theme config intended. One line, one time, at init.
  try { console.log('[BokBo] API base resolved to:', BOKBO.apiUrl); } catch (_) {}

  /**
   * Whether the native BokBo wishlist UI should render on this store.
   *
   * Checks two sources because we have two settings delivery paths:
   *   1. BOKBO.wishlistEnabled — populated by applySettings() after the
   *      XHR to /settings/:storeId resolves (fallback path, every store).
   *   2. window.__BOKBO_SETTINGS.wishlistEnabled — set synchronously by
   *      the /apps/bokbo/settings.js app-proxy snippet when the merchant
   *      has installed it (fast path, no XHR race).
   *
   * Defaults to enabled when neither source explicitly says `false`.
   */
  function isWishlistEnabled() {
    if (BOKBO.wishlistEnabled === false) return false;
    if (window.__BOKBO_SETTINGS && window.__BOKBO_SETTINGS.wishlistEnabled === false) return false;
    return true;
  }

  // ── Native BokBo Wishlist ───────────────────────────────────────
  //
  // State lives in localStorage under `bokbo_wishlist` (array of items)
  // and `bokbo_wishlist_share_id` (stable per-browser id used to build
  // shareable links). Every toggle best-effort syncs to the server at
  // POST /wishlist/:shareId so a shared link on another device can
  // read the same list. The sync is fire-and-forget — a failed request
  // does not affect the local UI, which remains the source of truth.
  //
  // The server side stores shared wishlists in Redis with a 30-day TTL;
  // if the key expires, the next toggle re-writes it.
  var WISHLIST_LS_KEY = 'bokbo_wishlist';
  var WISHLIST_SHARE_LS_KEY = 'bokbo_wishlist_share_id';
  var WISHLIST_SYNC_URL = BOKBO.apiUrl.replace(/\/search\/?$/, '') + '/wishlist/';

  function getWishlist() {
    try {
      var raw = localStorage.getItem(WISHLIST_LS_KEY) || '[]';
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function getWishlistShareId() {
    try {
      var id = localStorage.getItem(WISHLIST_SHARE_LS_KEY);
      if (!id) {
        // 9 base-36 chars ≈ 46 bits. Non-cryptographic; we only need
        // collision resistance at the per-store scale and the ID is
        // scoped to the device anyway.
        id = Math.random().toString(36).slice(2, 11);
        localStorage.setItem(WISHLIST_SHARE_LS_KEY, id);
      }
      return id;
    } catch (_) {
      // localStorage unavailable (private mode, disabled, quota) —
      // generate an ephemeral id so the rest of the session still
      // works, but don't try to persist it.
      return Math.random().toString(36).slice(2, 11);
    }
  }

  /** Fire-and-forget sync of the current wishlist to the server. */
  function syncWishlistToServer(wishlist) {
    var shareId = getWishlistShareId();
    try {
      fetch(WISHLIST_SYNC_URL + encodeURIComponent(shareId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wishlist: wishlist,
          store_id: BOKBO.storeId
            || (window.BOKBO_CONFIG && window.BOKBO_CONFIG.storeId)
            || (window.__BOKBO_SETTINGS && window.__BOKBO_SETTINGS.storeId),
        }),
      }).catch(function () {
        // Silent — the local wishlist is authoritative. A dropped
        // sync means the shareable link may be stale, but the
        // shopper's own device keeps working.
      });
    } catch (_) { /* fetch unavailable */ }
  }

  /** True iff the given shopify_id is currently in the wishlist. */
  function isWishlisted(shopifyId) {
    if (!shopifyId) return false;
    var list = getWishlist();
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].product_id === shopifyId) return true;
    }
    return false;
  }

  /**
   * Toggle wishlist membership for the given product. Returns true if
   * the product was added (useful for callers that want to show a
   * "saved" vs "removed" toast).
   *
   * Note on the lookup key: we store `product_id` in each entry but
   * the product object we receive from the search API carries
   * `shopify_id` (the legacy naming). The comparison below deliberately
   * matches storage's `product_id` against the incoming `shopify_id`.
   */
  function toggleWishlist(product) {
    if (!product || !product.shopify_id) return false;
    var wishlist = getWishlist();
    var idx = -1;
    for (var i = 0; i < wishlist.length; i++) {
      if (wishlist[i] && wishlist[i].product_id === product.shopify_id) {
        idx = i;
        break;
      }
    }
    var added;
    if (idx === -1) {
      wishlist.push({
        product_id: product.shopify_id,
        handle: product.handle || '',
        title: product.title || '',
        price: product.price || 0,
        image_url: product.image_url || '',
        vendor: product.vendor || '',
        added_at: Date.now(),
      });
      added = true;
    } else {
      wishlist.splice(idx, 1);
      added = false;
    }
    try {
      localStorage.setItem(WISHLIST_LS_KEY, JSON.stringify(wishlist));
    } catch (_) { /* quota / disabled — still sync to server below */ }
    syncWishlistToServer(wishlist);
    updateHeartButtons();
    return added;
  }

  /**
   * Reconcile heart-button paint state against the current wishlist.
   * Called after every card batch injects (infinite scroll) and after
   * every toggle — cheap querySelectorAll + class toggle, O(visible).
   * Also picks up cross-tab drift when another BokBo tab toggled a
   * heart (we don't listen for `storage` events but a fresh render
   * pulls from localStorage which is already in sync).
   */
  function updateHeartButtons() {
    var ids = Object.create(null);
    var list = getWishlist();
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].product_id) ids[list[i].product_id] = true;
    }
    var btns = document.querySelectorAll('.bokbo-heart-btn');
    for (var b = 0; b < btns.length; b++) {
      var id = btns[b].getAttribute('data-product-id');
      btns[b].classList.toggle('bokbo-heart-active', !!ids[id]);
    }
  }

  /**
   * Transient toast that fades in, holds for ~1.5s, then fades out.
   * Single toast element is reused across calls so rapid toggles
   * don't stack.
   */
  function showWishlistToast(message) {
    var toast = document.querySelector('.bokbo-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bokbo-wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    // Force layout so the opacity transition applies even when the
    // toast was just created inline (without this, adding .visible on
    // the same tick can skip the fade).
    void toast.offsetWidth;
    toast.classList.add('visible');
    clearTimeout(showWishlistToast._timer);
    showWishlistToast._timer = setTimeout(function () {
      toast.classList.remove('visible');
    }, 1500);
  }

  /**
   * Build a "Share my picks" button for the results header. Clicking
   * copies the shareable URL to the clipboard and shows a toast. The
   * URL points at /pages/bokbo-wishlist (a page the merchant installs
   * separately) and carries the ?shared=<id> token the server route
   * uses to look up the saved wishlist.
   */
  function renderShareButton() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bokbo-share-btn';
    btn.textContent = '\u2661 Share my picks';
    btn.addEventListener('click', function () {
      var shareId = getWishlistShareId();
      var url = window.location.origin + '/pages/bokbo-wishlist?shared=' + encodeURIComponent(shareId);
      var copy = navigator.clipboard && navigator.clipboard.writeText
        ? navigator.clipboard.writeText(url)
        : Promise.reject(new Error('clipboard unavailable'));
      copy.then(
        function () { showWishlistToast('Link copied!'); },
        function () {
          // Fallback: drop a window.prompt so the user can copy
          // manually. Better than silently failing on a browser
          // without clipboard-write permission.
          try { window.prompt('Copy this link:', url); } catch (_) {}
        }
      );
    });
    return btn;
  }

  // ── Wishlist page renderer ─────────────────────────────────────
  //
  // Mounts inside #bokbo-wishlist-shell (rendered by the Liquid
  // template). Two modes:
  //
  //   Owner mode     — URL has no ?shared param. Reads the shopper's
  //                    local wishlist from localStorage. Each card
  //                    gets a ✕ remove button; the heart / save
  //                    machinery is suppressed (would be redundant
  //                    with the current "is saved" state).
  //
  //   Shared mode    — URL has ?shared=<id>. Fetches that shared
  //                    list from GET /wishlist/:shareId. Cards get
  //                    an "Add to my list" button instead of the ✕.
  //                    A banner notifies the shopper they're viewing
  //                    someone else's curated set.
  //
  // Rendering reuses the existing buildCardHtml() for identical card
  // layout across search results and wishlist. Wishlist-only action
  // buttons are appended to the card via a body-level delegate.
  function initWishlistPage() {
    injectStyles();
    var shell = document.getElementById('bokbo-wishlist-shell');
    var gridEl = document.getElementById('bokbo-wishlist-grid');
    var emptyEl = document.getElementById('bokbo-wishlist-empty');
    var titleEl = document.getElementById('bokbo-wishlist-title');
    var bannerEl = document.getElementById('bokbo-wishlist-banner');
    var shareBtn = document.getElementById('bokbo-wishlist-share-btn');
    if (!shell || !gridEl) {
      console.warn('[BokBo Wishlist] shell / grid not found — aborting init');
      return;
    }

    // Point the module-level `grid` at the wishlist grid so the
    // shared buildCardHtml + quick-view delegate machinery hooks up.
    grid = gridEl;
    ensureQuickViewListener();

    var params = new URLSearchParams(window.location.search);
    var sharedId = params.get('shared');

    if (sharedId) {
      renderSharedWishlist(sharedId, gridEl, emptyEl, titleEl, bannerEl, shareBtn);
    } else {
      renderOwnerWishlist(gridEl, emptyEl, titleEl, bannerEl, shareBtn);
    }
  }

  function renderOwnerWishlist(gridEl, emptyEl, titleEl, bannerEl, shareBtn) {
    var items = getWishlist();
    filteredResults = items.map(function (it) {
      // Normalize the wishlist entry shape into what buildCardHtml
      // expects. Missing fields (available_sizes, all_tags) are OK —
      // the card renderer tolerates empty arrays / strings and just
      // omits the corresponding UI elements (size pills, etc.).
      return {
        shopify_id: it.product_id,
        handle: it.handle,
        title: it.title,
        price: it.price,
        image_url: it.image_url,
        vendor: it.vendor,
        url: it.handle ? '/products/' + it.handle : '',
        available_sizes: [],
        product_type: '',
        all_tags: [],
      };
    });

    if (titleEl) {
      titleEl.textContent = 'My Saved Items (' + items.length + ')';
    }
    if (bannerEl) bannerEl.innerHTML = '';

    if (items.length === 0) {
      gridEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      if (shareBtn) shareBtn.style.display = 'none';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    paintWishlistGrid(gridEl, filteredResults, { ownerMode: true });

    if (shareBtn) {
      shareBtn.style.display = '';
      shareBtn.addEventListener('click', function () {
        var shareId = getWishlistShareId();
        var url = window.location.origin + '/pages/bokbo-wishlist?shared=' + encodeURIComponent(shareId);
        var copy = navigator.clipboard && navigator.clipboard.writeText
          ? navigator.clipboard.writeText(url)
          : Promise.reject(new Error('clipboard unavailable'));
        copy.then(
          function () { showWishlistToast('Link copied!'); },
          function () { try { window.prompt('Copy this link:', url); } catch (_) {} }
        );
      });
    }
  }

  function renderSharedWishlist(shareId, gridEl, emptyEl, titleEl, bannerEl, shareBtn) {
    gridEl.innerHTML = '<div style="padding:40px;text-align:center;color:#666">Loading saved list\u2026</div>';
    if (emptyEl) emptyEl.style.display = 'none';
    if (shareBtn) shareBtn.style.display = 'none';

    var url = BOKBO.apiUrl.replace(/\/search\/?$/, '') + '/wishlist/' + encodeURIComponent(shareId);
    fetch(url)
      .then(function (r) {
        if (r.status === 404) throw new Error('Wishlist not found');
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var items = Array.isArray(data && data.wishlist) ? data.wishlist : [];
        if (titleEl) {
          titleEl.textContent = 'Saved picks (' + items.length + ')';
        }
        if (bannerEl) {
          bannerEl.innerHTML =
            '<div class="bokbo-wishlist-shared-banner">' +
              'Viewing someone\u2019s saved picks \u2014 tap \u201CAdd to my list\u201D on any product to save it to your own wishlist.' +
            '</div>';
        }
        if (items.length === 0) {
          gridEl.innerHTML = '';
          if (emptyEl) {
            emptyEl.style.display = '';
            var linkEl = emptyEl.querySelector('a');
            if (linkEl) linkEl.textContent = 'Browse products';
          }
          return;
        }
        filteredResults = items.map(function (it) {
          return {
            shopify_id: it.product_id,
            handle: it.handle,
            title: it.title,
            price: it.price,
            image_url: it.image_url,
            vendor: it.vendor,
            url: it.handle ? '/products/' + it.handle : '',
            available_sizes: [],
            product_type: '',
            all_tags: [],
          };
        });
        paintWishlistGrid(gridEl, filteredResults, { ownerMode: false });
      })
      .catch(function (err) {
        console.warn('[BokBo Wishlist] shared fetch failed:', err && err.message);
        gridEl.innerHTML =
          '<div style="padding:40px;text-align:center;color:#666">' +
            'Couldn\u2019t load that shared list. ' +
            '<a href="/pages/bokbo-search" style="color:#b40ad6">Go to search</a>' +
          '</div>';
      });
  }

  function paintWishlistGrid(gridEl, items, opts) {
    opts = opts || {};
    var ownerMode = !!opts.ownerMode;

    // Apply the same grid layout the search results use so cards are
    // identically sized on the wishlist page. Applied via style attr
    // so we don't depend on .bokbo-results-grid being present in the
    // DOM above.
    gridEl.style.display = 'grid';
    gridEl.style.gridTemplateColumns = 'repeat(auto-fill, minmax(220px, 1fr))';
    gridEl.style.gap = '16px';

    var frag = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) {
      var r = items[i];
      var wrapper = document.createElement('div');
      wrapper.className = 'bokbo-wishlist-card-wrapper';
      wrapper.style.position = 'relative';
      wrapper.innerHTML = buildCardHtml(r);
      // Owner-mode cards get a ✕ remove button; shared-mode cards
      // get an "Add to my list" button. Rendered as absolutely-
      // positioned chips so they float over the card without
      // reflowing its layout.
      var actionBtn = document.createElement('button');
      actionBtn.type = 'button';
      actionBtn.setAttribute('data-product-id', r.shopify_id || '');
      if (ownerMode) {
        actionBtn.className = 'bokbo-wishlist-remove-btn';
        actionBtn.setAttribute('aria-label', 'Remove ' + (r.title || 'item') + ' from list');
        actionBtn.innerHTML = '\u00d7';
        actionBtn.style.cssText =
          'position:absolute;top:8px;left:8px;z-index:11;' +
          'width:28px;height:28px;border-radius:50%;' +
          'background:rgba(255,255,255,0.95);border:1px solid #ddd;' +
          'font-size:18px;line-height:1;cursor:pointer;color:#333;';
      } else {
        actionBtn.className = 'bokbo-wishlist-add-btn';
        actionBtn.setAttribute('data-handle', r.handle || '');
        actionBtn.setAttribute('data-title', r.title || '');
        actionBtn.textContent = '+ Add to my list';
        actionBtn.style.cssText =
          'position:absolute;bottom:8px;left:8px;right:8px;z-index:11;' +
          'padding:8px 12px;border-radius:6px;background:#b40ad6;color:#fff;' +
          'border:none;font-size:13px;font-weight:600;cursor:pointer;';
      }
      wrapper.appendChild(actionBtn);
      frag.appendChild(wrapper);
    }
    gridEl.innerHTML = '';
    gridEl.appendChild(frag);

    // Body-level delegate for remove / add buttons. Registered every
    // paint rather than once on init so the listener stays bound to
    // the current `gridEl` / `filteredResults` closure — minimal
    // cost, keeps the state flow obvious.
    if (!paintWishlistGrid._wired) {
      paintWishlistGrid._wired = true;
      document.body.addEventListener('click', function (e) {
        var removeBtn = e.target && e.target.closest && e.target.closest('.bokbo-wishlist-remove-btn');
        var addBtn = e.target && e.target.closest && e.target.closest('.bokbo-wishlist-add-btn');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          var pid = removeBtn.getAttribute('data-product-id');
          if (!pid) return;
          // Reuse toggleWishlist — it removes when already present.
          toggleWishlist({ shopify_id: pid });
          showWishlistToast('Removed');
          // Re-paint the owner list without a full page reload so
          // the UX feels snappy.
          var freshShell = document.getElementById('bokbo-wishlist-shell');
          var freshGrid = document.getElementById('bokbo-wishlist-grid');
          var freshEmpty = document.getElementById('bokbo-wishlist-empty');
          var freshTitle = document.getElementById('bokbo-wishlist-title');
          var freshBanner = document.getElementById('bokbo-wishlist-banner');
          var freshShareBtn = document.getElementById('bokbo-wishlist-share-btn');
          if (freshGrid && freshShell) {
            renderOwnerWishlist(freshGrid, freshEmpty, freshTitle, freshBanner, freshShareBtn);
          }
        } else if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          var product = {
            shopify_id: addBtn.getAttribute('data-product-id'),
            handle: addBtn.getAttribute('data-handle') || '',
            title: addBtn.getAttribute('data-title') || '',
            price: 0,
            image_url: '',
            vendor: '',
          };
          if (!product.shopify_id) return;
          var added = toggleWishlist(product);
          showWishlistToast(added ? '\u2665 Saved to your list' : 'Removed from your list');
        }
      });
    }
  }

  // ── Settings fetch (30s in-memory cache, 2000ms timeout, shared) ──
  // Timeout is 2000ms (not 500ms) because the settings endpoint can be slow
  // on cold starts / under load. At 500ms a slow response would fall through
  // to the defaults (searchEnabled:true) and run init(), which silently
  // overrides the admin toggle. 2s is long enough to survive a cold start
  // but short enough that users never see a visibly blank search page — the
  // anti-flash safety net at 3s still guarantees the page becomes visible.
  var SETTINGS_CACHE_KEY = '__bokboSettingsCache';
  var SETTINGS_CACHE_TTL = 30 * 1000;
  // Settings-fetch timeout (Fix 4 — speed). Shortened from 2000ms to
  // 500ms so the widget never blocks the initial search on a slow
  // /settings GET. After 500ms we fall back to compile-time defaults
  // and start the search anyway; if the settings eventually arrive and
  // the shopper hasn't flipped a toggle they'd see in the first paint,
  // the behavior is indistinguishable.
  var SETTINGS_TIMEOUT_MS = 500;

  function getSettingsUrl() {
    return BOKBO.apiUrl.replace(/\/search\/?$/, '') +
      '/settings/' + encodeURIComponent(BOKBO.storeId);
  }

  function fetchSettings(cb) {
    var cache = window[SETTINGS_CACHE_KEY];
    if (cache && (Date.now() - cache.fetchedAt) < SETTINGS_CACHE_TTL) {
      return cb(cache.data);
    }

    var done = false;
    var xhr = new XMLHttpRequest();
    var timer = setTimeout(function () {
      if (done) return;
      done = true;
      try { xhr.abort(); } catch (_) {}
      console.warn('[BokBo Results] Settings fetch timed out (>2000ms) — using defaults');
      cb(null);
    }, SETTINGS_TIMEOUT_MS);

    try {
      xhr.open('GET', getSettingsUrl(), true);
    } catch (e) {
      clearTimeout(timer);
      done = true;
      return cb(null);
    }

    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4 || done) return;
      done = true;
      clearTimeout(timer);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var data = JSON.parse(xhr.responseText);
          var settings = (data && data.settings) || null;
          if (settings) {
            window[SETTINGS_CACHE_KEY] = { data: settings, fetchedAt: Date.now() };
            return cb(settings);
          }
        } catch (_) {}
      }
      console.warn('[BokBo Results] Settings fetch failed (status ' + xhr.status + ') — using defaults');
      cb(null);
    };

    xhr.send();
  }

  function applySettings(s) {
    if (!s) return;
    if (typeof s.searchEnabled   === 'boolean') BOKBO.searchEnabled   = s.searchEnabled;
    if (typeof s.infiniteScroll  === 'boolean') BOKBO.infiniteScroll  = s.infiniteScroll;
    if (typeof s.skeletonLoading === 'boolean') BOKBO.skeletonLoading = s.skeletonLoading;
    if (typeof s.wishlistEnabled === 'boolean') BOKBO.wishlistEnabled = s.wishlistEnabled;
    if (typeof s.resultsPerPage  === 'number')  BOKBO.perPage         = s.resultsPerPage;
    if (typeof s.accentColor     === 'string' && /^#[0-9a-fA-F]{6}$/.test(s.accentColor)) {
      BOKBO.accentColor = s.accentColor;
      document.documentElement.style.setProperty('--bokbo-accent', s.accentColor);
    }
  }

  // ── Inline-navigation event bridge (Part 6 — flash elimination) ─
  //
  // When the shopper submits a new search, bokbo-search-v3 dispatches a
  // `bokbo:navigate` CustomEvent on document BEFORE doing any hard
  // redirect. If we're already booted on this page (the usual case
  // when the shopper is searching AGAIN from inside results), we can
  // claim the event, re-render inline via runResearch(), and let the
  // source side pushState the URL — no page load, no 404 flash.
  //
  // If the source side dispatches but we're NOT on a renderable page
  // (e.g. shopper searched from a homepage that has no BokBo shell),
  // we leave the event undefaulted so the source side falls through
  // to its normal window.location.replace() — flash still eliminated
  // in that case by the pre-skeleton Liquid shell from the earlier
  // task.
  //
  // popstate mirrors the same flow for back/forward between pushState'd
  // BokBo searches. If the popped state has no bokboQuery (we just
  // popped past our initial pushState into a pre-BokBo entry), we
  // force a reload so the browser re-renders whatever page the
  // shopper was on before they searched.
  //
  // These listeners are registered BEFORE the isSearchPage() early
  // return below, because the event may fire from a script on a
  // non-search page (the same page that hosts bokbo-search-v3).
  document.addEventListener('bokbo:navigate', function (e) {
    var q = e && e.detail && e.detail.query;
    console.log('[BokBo] bokbo:navigate event received — query:', JSON.stringify(q),
      '| container-bound:', !!container, '| will-handle-inline:', !!(q && container));
    if (!q || !container) {
      // Not booted here — let the source side do a hard nav.
      return;
    }
    if (e.preventDefault) e.preventDefault();
    if (e.detail) e.detail.handled = true;
    runResearch(String(q).trim(), null);
  });

  window.addEventListener('popstate', function (e) {
    var state = e && e.state;
    if (state && state.bokboQuery && container) {
      runResearch(String(state.bokboQuery).trim(), null);
      return;
    }
    // Popped past all of our pushState entries OR we aren't booted
    // on this page. Force a real load so the browser restores
    // whatever the URL now points at.
    if (container) {
      try { window.location.reload(); } catch (_) {}
    }
  });

  // ── Wishlist page — separate boot path ──────────────────────────
  // The /pages/bokbo-wishlist template renders its own shell (section
  // liquid) and is populated by initWishlistPage below. Different flow
  // from the search pages (no query, no filter bar, different card
  // actions), so it early-returns here after running its own init.
  if (isWishlistPage()) {
    initWishlistPage();
    return;
  }

  // ── Only run on search pages ────────────────────────────────────
  // Dedicated BokBo page is preferred; legacy /search and
  // /pages/search-results remain supported as fallbacks in case a shopper
  // lands there directly (external link, bookmark, or theme that bypasses
  // our form intercept).
  if (!isSearchPage()) return;

  // ── Read the query ──────────────────────────────────────────────
  // Prefer the URL `?q=` parameter when present — it's the canonical,
  // shareable source of truth. Fall back to sessionStorage['bokbo_query']
  // only when the URL doesn't have a query, which is how the dropdown
  // widget signals the intended search across the navigation. Once we
  // consume the stashed value we clear it so a later navigation without
  // a q param doesn't accidentally re-trigger a stale search.
  var params = new URLSearchParams(window.location.search);
  var query = (params.get('q') || '').trim();
  if (!query) {
    try {
      var stashed = window.sessionStorage.getItem('bokbo_query');
      if (stashed && stashed.trim()) {
        query = stashed.trim();
        window.sessionStorage.removeItem('bokbo_query');
      }
    } catch (_) {
      // sessionStorage unavailable (private mode, etc.) — just skip
    }
  }

  if (!query) return; // nothing to search

  // ── Keep the dedicated page out of search engine indexes ────────
  // /pages/bokbo-search produces endless duplicate URLs (one per query),
  // and its content is 100% JS-rendered, so there is no SEO value in
  // indexing it — add a robots noindex/nofollow meta at runtime. The
  // legacy /search path is already noindexed by Shopify by default.
  if (isBokboSearchPage()) {
    try {
      var meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      (document.head || document.documentElement).appendChild(meta);
    } catch (_) {}
  }

  // Signal to bokbo-search.js that the results page is active.
  // The dropdown widget checks this to know it doesn't need to
  // intercept form submissions on this page (we handle it).
  window.__bokboResultsActive = true;

  console.log('[BokBo Results] Query:', query);

  // ── Helpers ─────────────────────────────────────────────────────
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function formatPrice(price) {
    if (price == null || isNaN(price)) return '';
    return '$' + Number(price).toFixed(2);
  }

  // ── Styles ──────────────────────────────────────────────────────
  var STYLES = '\
    /* Hide the theme\'s breadcrumbs on BokBo search results.\
       The breadcrumb block is rendered outside the native results\
       container BokBo replaces, so it survives init() unless we\
       explicitly hide it. Covers the common Dawn / Shopify selectors;\
       safe to leave in place — the BokBo results page already shows\
       its own heading + filter bar, so a breadcrumb is redundant. */\
    .breadcrumb,\
    .breadcrumbs,\
    nav.breadcrumb,\
    nav.breadcrumbs,\
    nav[aria-label="breadcrumbs" i],\
    nav[aria-label*="breadcrumb" i],\
    nav[aria-label="Breadcrumb navigation"],\
    .breadcrumbs__list,\
    ol.breadcrumbs__list,\
    .page-breadcrumb,\
    .shopify-section--breadcrumbs,\
    #shopify-section-breadcrumbs,\
    [data-breadcrumbs],\
    ol.breadcrumb,\
    ul.breadcrumbs {\
      display: none !important;\
    }\
    /* RackHunt-specific: highest specificity override for their exact markup.\
       <nav class="breadcrumbs" aria-label="Breadcrumb navigation">\
         <ol class="breadcrumbs__list"> */\
    html body nav.breadcrumbs,\
    html body nav.breadcrumbs .breadcrumbs__list,\
    html body nav.breadcrumbs ol.breadcrumbs__list,\
    html body nav[aria-label="Breadcrumb navigation"] {\
      display: none !important;\
      visibility: hidden !important;\
      height: 0 !important;\
      overflow: hidden !important;\
      margin: 0 !important;\
      padding: 0 !important;\
    }\
    .bokbo-results-container {\
      max-width: 1200px;\
      margin: 0 auto;\
      padding: 24px 16px 60px;\
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;\
    }\
    .bokbo-results-header {\
      margin-bottom: 24px;\
    }\
    .bokbo-results-header h1 {\
      font-size: 24px;\
      font-weight: 600;\
      color: #1a1a1a;\
      margin: 0 0 4px;\
    }\
    .bokbo-results-header p {\
      font-size: 14px;\
      color: #777;\
      margin: 0;\
    }\
    .bokbo-results-search-box {\
      display: flex;\
      align-items: center;\
      gap: 8px;\
      margin-top: 12px;\
      max-width: 480px;\
    }\
    .bokbo-results-search-box input {\
      flex: 1;\
      padding: 8px 12px;\
      border: 1px solid #ddd;\
      border-radius: 6px;\
      font-size: 15px;\
      color: #1a1a1a;\
      background: #fff;\
      outline: none;\
      transition: border-color 0.15s ease, box-shadow 0.15s ease;\
    }\
    .bokbo-results-search-box input:focus {\
      border-color: var(--bokbo-accent, #D63031);\
      box-shadow: 0 0 0 2px rgba(214,48,49,0.12);\
    }\
    @keyframes bokbo-results-pulse {\
      0%, 100% { box-shadow: 0 0 0 0 rgba(214,48,49,0), inset 0 0 0 1px rgba(214,48,49,0.10); }\
      50%       { box-shadow: 0 0 0 3px rgba(214,48,49,0.18), inset 0 0 0 1px rgba(214,48,49,0.35); }\
    }\
    .bokbo-results-search-box input.bokbo-searching {\
      animation: bokbo-results-pulse 1s ease-in-out infinite !important;\
    }\
    .bokbo-results-grid {\
      display: grid;\
      grid-template-columns: repeat(4, 1fr);\
      gap: 20px;\
      align-content: start;\
    }\
    @media (max-width: 1024px) {\
      .bokbo-results-grid {\
        grid-template-columns: repeat(3, 1fr);\
      }\
    }\
    @media (max-width: 768px) {\
      .bokbo-results-grid {\
        grid-template-columns: repeat(2, 1fr);\
        gap: 12px;\
      }\
    }\
    .bokbo-results-card-outer {\
      position: relative;\
      display: flex;\
      flex-direction: column;\
      background: #fff;\
      border: 1px solid #e8e8e8;\
      border-radius: 10px;\
      overflow: hidden;\
      transition: box-shadow 0.2s ease, transform 0.2s ease;\
      min-height: 0;\
    }\
    .bokbo-results-card-outer:hover {\
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);\
      transform: translateY(-2px);\
    }\
    .bokbo-results-card-wrap {\
      display: flex;\
      flex-direction: column;\
      flex: 1;\
    }\
    .bokbo-results-card {\
      display: flex;\
      flex-direction: column;\
      text-decoration: none;\
      color: inherit;\
      flex: 1;\
    }\
    .bokbo-results-card-img-wrap {\
      position: relative;\
      width: 100%;\
      padding-top: 100%;\
      background: #f5f5f5;\
      overflow: hidden;\
    }\
    .bokbo-results-card-img {\
      position: absolute;\
      top: 0;\
      left: 0;\
      width: 100%;\
      height: 100%;\
      object-fit: cover;\
    }\
    .bokbo-results-card-img-placeholder {\
      position: absolute;\
      top: 0;\
      left: 0;\
      width: 100%;\
      height: 100%;\
      display: flex;\
      align-items: center;\
      justify-content: center;\
      font-size: 40px;\
      color: #ccc;\
    }\
    .bokbo-results-card-body {\
      padding: 12px 14px 16px;\
      display: flex;\
      flex-direction: column;\
      flex: 1;\
      gap: 4px;\
    }\
    /* Brand name rendered at the top of each card (replaces the old\
       category/breadcrumb text). Clickable — delegated handler routes\
       to /pages/bokbo-search for that brand. Styled in brand purple. */\
    .bokbo-card-brand {\
      font-size: 11px;\
      font-weight: 600;\
      color: #b40ad6;\
      text-transform: uppercase;\
      letter-spacing: 0.05em;\
      margin-bottom: 4px;\
      cursor: pointer;\
    }\
    .bokbo-card-brand:hover {\
      text-decoration: underline;\
    }\
    .bokbo-card-brand:focus-visible {\
      outline: 2px solid #b40ad6;\
      outline-offset: 2px;\
    }\
    .bokbo-results-card-title {\
      font-size: 14px;\
      font-weight: 500;\
      line-height: 1.4;\
      color: #1a1a1a;\
      display: -webkit-box;\
      -webkit-line-clamp: 2;\
      -webkit-box-orient: vertical;\
      overflow: hidden;\
    }\
    .bokbo-results-card-price {\
      margin-top: auto;\
      padding-top: 8px;\
      font-size: 16px;\
      font-weight: 700;\
      color: #1a1a1a;\
    }\
    /* Size pills on each card. Shows the shopper what sizes the\
       product actually has IN STOCK before they click. When a\
       size-filter search is active (activeFilters.size_*), the\
       matching pill lights up in BokBo purple via .bokbo-size-active\
       so the reassurance that "this card carries your size" is\
       visible at a glance. */\
    .bokbo-card-sizes {\
      display: flex;\
      flex-wrap: wrap;\
      gap: 4px;\
      margin-top: 6px;\
    }\
    .bokbo-size-pill {\
      font-size: 10px;\
      padding: 2px 6px;\
      border: 1px solid #ddd;\
      border-radius: 3px;\
      color: #666;\
      background: #fafafa;\
    }\
    .bokbo-size-pill.bokbo-size-active {\
      border-color: #b40ad6;\
      color: #b40ad6;\
      background: #f9f0ff;\
      font-weight: 600;\
    }\
    .bokbo-size-more {\
      font-size: 10px;\
      color: #999;\
      padding: 2px 4px;\
    }\
    /* "Other sizes available" note — rendered ONLY on cards that\
       survived the size-filter relaxation cascade (the requested size\
       is not in available_sizes). Tiny, italic, gray so it keeps the\
       card visually quiet; loud enough that shoppers know why a card\
       in "M" results is actually only stocked in L and XL. */\
    .bokbo-size-note {\
      font-size: 9px;\
      color: #999;\
      text-align: center;\
      margin-top: 2px;\
      font-style: italic;\
    }\
    /* Card action row — reserves space for Quick View and any future\
       inline buttons. Still referenced by the Quick View markup. */\
    .bokbo-results-card-actions {\
      display: flex;\
      align-items: center;\
      gap: 6px;\
      margin-top: 8px;\
    }\
    /* Native BokBo wishlist heart. Absolutely positioned top-right of\
       the card image, white circular chip with a soft drop shadow.\
       Idle stroke is a neutral grey so the heart reads as a "save"\
       affordance; active state fills + strokes in BokBo pink. Pulse\
       class is toggled by the click delegate + the quick-view\
       swipe-up gesture to celebrate the save. */\
    .bokbo-heart-btn {\
      position: absolute;\
      top: 8px;\
      right: 8px;\
      background: white;\
      border: none;\
      border-radius: 50%;\
      width: 36px;\
      height: 36px;\
      display: flex;\
      align-items: center;\
      justify-content: center;\
      cursor: pointer;\
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);\
      transition: transform 0.2s ease;\
      z-index: 10;\
    }\
    .bokbo-heart-btn:hover { transform: scale(1.1); }\
    .bokbo-heart-btn svg {\
      width: 18px;\
      height: 18px;\
      fill: none;\
      stroke: #999;\
      stroke-width: 2;\
      transition: all 0.2s ease;\
    }\
    .bokbo-heart-btn.bokbo-heart-active svg { fill: #ff3366; stroke: #ff3366; }\
    .bokbo-heart-btn.bokbo-heart-pulse { animation: bokbo-heart-pulse 0.3s ease; }\
    @keyframes bokbo-heart-pulse {\
      0% { transform: scale(1); }\
      50% { transform: scale(1.3); }\
      100% { transform: scale(1); }\
    }\
    /* "Share my picks" button shown in the results header. Pill\
       chip style, subtle pink on hover. */\
    .bokbo-share-btn {\
      background: none;\
      border: 1px solid #ddd;\
      border-radius: 20px;\
      padding: 6px 14px;\
      font-size: 12px;\
      cursor: pointer;\
      color: #666;\
      transition: all 0.2s;\
    }\
    .bokbo-share-btn:hover { border-color: #ff3366; color: #ff3366; }\
    /* Transient top-center toast used for wishlist add/remove +\
       share-link-copied notifications. Pinned to fixed coords so it\
       never intercepts clicks and always lands above the modal. */\
    .bokbo-wishlist-toast {\
      position: fixed;\
      top: 20px;\
      left: 50%;\
      transform: translateX(-50%);\
      background: rgba(0,0,0,0.8);\
      color: white;\
      padding: 8px 20px;\
      border-radius: 20px;\
      font-size: 13px;\
      z-index: 99999;\
      opacity: 0;\
      transition: opacity 0.3s;\
      pointer-events: none;\
    }\
    .bokbo-wishlist-toast.visible { opacity: 1; }\
    /* Quick View button */\
    .bokbo-results-card-outer .bokbo-quick-view-btn {\
      display: inline-flex;\
      align-items: center;\
      justify-content: center;\
      padding: 6px 12px;\
      font-size: 12px;\
      font-weight: 500;\
      color: #333;\
      background: #f5f5f5;\
      border: 1px solid #ddd;\
      border-radius: 4px;\
      cursor: pointer;\
      transition: background 0.15s ease, border-color 0.15s ease;\
      text-decoration: none;\
    }\
    .bokbo-results-card-outer .bokbo-quick-view-btn:hover {\
      background: #eee;\
      border-color: #ccc;\
    }\
    /* ── BokBo Quick View Modal ── */\
    .bokbo-qv-overlay {\
      position: fixed;\
      inset: 0;\
      z-index: 99999;\
      background: rgba(0,0,0,0.5);\
      display: flex;\
      align-items: center;\
      justify-content: center;\
      opacity: 0;\
      transition: opacity 0.2s ease;\
    }\
    .bokbo-qv-overlay.bokbo-qv-visible {\
      opacity: 1;\
    }\
    .bokbo-qv-dialog {\
      position: relative;\
      background: #fff;\
      border-radius: 8px;\
      max-width: 680px;\
      width: 92vw;\
      max-height: 88vh;\
      overflow-y: auto;\
      box-shadow: 0 20px 60px rgba(0,0,0,0.25);\
      padding: 0;\
    }\
    .bokbo-qv-close {\
      position: absolute;\
      top: 12px;\
      right: 12px;\
      z-index: 2;\
      background: #fff;\
      border: 1px solid #ddd;\
      border-radius: 50%;\
      width: 32px;\
      height: 32px;\
      cursor: pointer;\
      display: flex;\
      align-items: center;\
      justify-content: center;\
      font-size: 18px;\
      line-height: 1;\
      color: #333;\
      transition: background 0.15s;\
    }\
    .bokbo-qv-close:hover { background: #f0f0f0; }\
    /* Desktop navigation chevrons — positioned on either side of the\
       dialog so the shopper can page through the current results\
       array without closing. Hidden on narrow screens (< 768px) in\
       favour of the swipe gesture + mobile hint below. */\
    .bokbo-qv-nav {\
      position: absolute;\
      top: 50%;\
      transform: translateY(-50%);\
      z-index: 2;\
      background: rgba(255,255,255,0.92);\
      border: 1px solid #ddd;\
      border-radius: 50%;\
      width: 44px;\
      height: 44px;\
      display: flex;\
      align-items: center;\
      justify-content: center;\
      font-size: 26px;\
      line-height: 1;\
      color: #333;\
      cursor: pointer;\
      transition: opacity 0.15s, background 0.15s;\
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);\
    }\
    .bokbo-qv-nav:hover { background: #fff; }\
    .bokbo-qv-nav-prev { left: max(12px, calc(50vw - 380px)); }\
    .bokbo-qv-nav-next { right: max(12px, calc(50vw - 380px)); }\
    .bokbo-qv-nav-disabled {\
      opacity: 0.25;\
      cursor: default;\
      pointer-events: none;\
    }\
    /* Mobile swipe hint — fades out after 2s via JS. Visually gentle\
       so it does not compete with the product image. */\
    .bokbo-qv-swipe-hint {\
      position: absolute;\
      top: 12px;\
      left: 50%;\
      transform: translateX(-50%);\
      z-index: 2;\
      padding: 6px 14px;\
      background: rgba(0,0,0,0.55);\
      color: #fff;\
      font-size: 12px;\
      letter-spacing: 0.04em;\
      border-radius: 999px;\
      pointer-events: none;\
      transition: opacity 0.4s ease;\
    }\
    .bokbo-qv-swipe-hint.bokbo-qv-fade { opacity: 0; }\
    @media (max-width: 767px) {\
      .bokbo-qv-nav { display: none; }\
    }\
    @media (min-width: 768px) {\
      .bokbo-qv-swipe-hint { display: none; }\
    }\
    .bokbo-qv-body {\
      display: flex;\
      gap: 24px;\
      padding: 24px;\
    }\
    @media (max-width: 600px) {\
      .bokbo-qv-body { flex-direction: column; gap: 16px; padding: 16px; }\
    }\
    .bokbo-qv-image {\
      flex: 0 0 280px;\
      max-width: 280px;\
    }\
    @media (max-width: 600px) {\
      .bokbo-qv-image { flex: none; max-width: 100%; }\
    }\
    .bokbo-qv-image img {\
      width: 100%;\
      height: auto;\
      border-radius: 4px;\
      display: block;\
    }\
    .bokbo-qv-info {\
      flex: 1;\
      min-width: 0;\
    }\
    .bokbo-qv-title {\
      font-size: 18px;\
      font-weight: 600;\
      margin: 0 0 8px;\
      color: #111;\
      line-height: 1.3;\
    }\
    .bokbo-qv-vendor {\
      font-size: 13px;\
      color: #777;\
      margin-bottom: 12px;\
    }\
    .bokbo-qv-price {\
      font-size: 20px;\
      font-weight: 600;\
      color: #111;\
      margin-bottom: 4px;\
    }\
    .bokbo-qv-compare {\
      font-size: 14px;\
      color: #999;\
      text-decoration: line-through;\
      margin-bottom: 12px;\
    }\
    .bokbo-qv-desc {\
      font-size: 14px;\
      color: #444;\
      line-height: 1.5;\
      margin-bottom: 16px;\
      max-height: 120px;\
      overflow-y: auto;\
    }\
    .bokbo-qv-variants {\
      margin-bottom: 16px;\
    }\
    .bokbo-qv-variants label {\
      display: block;\
      font-size: 13px;\
      font-weight: 500;\
      color: #555;\
      margin-bottom: 6px;\
    }\
    .bokbo-qv-variants select {\
      width: 100%;\
      padding: 8px 10px;\
      border: 1px solid #ccc;\
      border-radius: 4px;\
      font-size: 14px;\
    }\
    .bokbo-qv-atc {\
      display: inline-block;\
      width: 100%;\
      padding: 12px 24px;\
      font-size: 15px;\
      font-weight: 600;\
      color: #fff;\
      background: var(--bokbo-accent, #D4390A);\
      border: none;\
      border-radius: 4px;\
      cursor: pointer;\
      text-align: center;\
      transition: opacity 0.15s;\
    }\
    .bokbo-qv-atc:hover { opacity: 0.9; }\
    .bokbo-qv-atc:disabled { opacity: 0.5; cursor: not-allowed; }\
    .bokbo-qv-link {\
      display: block;\
      text-align: center;\
      margin-top: 10px;\
      font-size: 13px;\
      color: #555;\
    }\
    .bokbo-qv-loading {\
      padding: 60px 24px;\
      text-align: center;\
      color: #999;\
      font-size: 14px;\
    }\
    .bokbo-results-loading {\
      display: flex;\
      flex-direction: column;\
      align-items: center;\
      justify-content: center;\
      padding: 80px 20px;\
      gap: 16px;\
    }\
    .bokbo-results-spinner {\
      width: 36px;\
      height: 36px;\
      border: 3px solid #e8e8e8;\
      border-top-color: var(--bokbo-accent, #D4390A);\
      border-radius: 50%;\
      animation: bokbo-spin 0.7s linear infinite;\
    }\
    @keyframes bokbo-spin {\
      to { transform: rotate(360deg); }\
    }\
    .bokbo-results-loading-text {\
      font-size: 14px;\
      color: #999;\
    }\
    .bokbo-results-empty {\
      text-align: center;\
      padding: 80px 20px;\
    }\
    .bokbo-results-empty h2 {\
      font-size: 20px;\
      font-weight: 600;\
      color: #1a1a1a;\
      margin: 0 0 8px;\
    }\
    .bokbo-results-empty p {\
      font-size: 14px;\
      color: #777;\
      margin: 0 0 20px;\
    }\
    .bokbo-results-empty .bokbo-search-again {\
      display: inline-block;\
      padding: 10px 24px;\
      background: var(--bokbo-accent, #D4390A);\
      color: #fff;\
      font-size: 14px;\
      font-weight: 600;\
      border: none;\
      border-radius: 6px;\
      cursor: pointer;\
      text-decoration: none;\
      transition: opacity 0.15s;\
    }\
    .bokbo-results-empty .bokbo-search-again:hover {\
      opacity: 0.85;\
    }\
    /* Dedicated empty-state for strict-size zero-results. Rendered\
       either inside #bokbo-grid (filter bar still visible) or at\
       container level (initial load). Matches the visual weight of\
       .bokbo-results-empty but stays centered even inside the grid\
       wrapper, which uses CSS grid. */\
    .bokbo-results-size-empty {\
      grid-column: 1 / -1;\
      text-align: center;\
      padding: 60px 20px;\
    }\
    .bokbo-results-size-empty h2 {\
      font-size: 20px;\
      font-weight: 600;\
      margin: 0 0 8px;\
      color: #1a1a1a;\
    }\
    .bokbo-results-size-empty p {\
      font-size: 14px;\
      color: #777;\
      margin: 0 0 20px;\
    }\
    .bokbo-results-size-empty .bokbo-clear-size-btn {\
      display: inline-block;\
      padding: 10px 24px;\
      background: var(--bokbo-accent, #D4390A);\
      color: #fff;\
      font-size: 14px;\
      font-weight: 600;\
      border: none;\
      border-radius: 6px;\
      cursor: pointer;\
      transition: opacity 0.15s;\
    }\
    .bokbo-results-size-empty .bokbo-clear-size-btn:hover {\
      opacity: 0.85;\
    }\
    /* Non-blocking notice at the top of the grid when the server\
       relaxed the size filter to surface SOME results instead of\
       returning an empty page. Pairs with the size pills on each\
       card — the notice explains why non-matching sizes are showing\
       up, the pills let the shopper see at a glance which cards\
       actually carry a size close to what they asked for. */\
    .bokbo-relax-notice {\
      grid-column: 1 / -1;\
      display: flex;\
      align-items: center;\
      justify-content: space-between;\
      gap: 12px;\
      margin: 0 0 12px;\
      padding: 10px 14px;\
      background: #f9f0ff;\
      border: 1px solid #e8d6f5;\
      border-left: 3px solid #b40ad6;\
      border-radius: 6px;\
      font-size: 13px;\
      color: #4a2766;\
    }\
    .bokbo-relax-notice-msg {\
      flex: 1;\
    }\
    .bokbo-relax-notice-close {\
      background: none;\
      border: none;\
      font-size: 18px;\
      line-height: 1;\
      color: #7a4a9e;\
      cursor: pointer;\
      padding: 0 4px;\
    }\
    .bokbo-relax-notice-close:hover { color: #b40ad6; }\
    .bokbo-results-footer {\
      text-align: center;\
      padding: 24px 0 0;\
      font-size: 12px;\
      color: #bbb;\
    }\
    .bokbo-results-footer a {\
      color: #bbb;\
      text-decoration: none;\
    }\
    .bokbo-results-footer a:hover {\
      color: var(--bokbo-accent, #D4390A);\
    }\
    .bokbo-results-error {\
      text-align: center;\
      padding: 60px 20px;\
      color: #999;\
      font-size: 14px;\
    }\
    .bokbo-filter-bar {\
      display: flex;\
      flex-direction: column;\
      gap: 10px;\
      margin-bottom: 20px;\
      padding: 14px 16px;\
      background: #fdf5f4;\
      border: 1px solid #f0d4cf;\
      border-radius: 10px;\
    }\
    .bokbo-filter-row {\
      display: flex;\
      flex-wrap: wrap;\
      align-items: center;\
      gap: 10px;\
      width: 100%;\
    }\
    .bokbo-filter-bar label {\
      font-size: 13px;\
      font-weight: 600;\
      color: #555;\
      white-space: nowrap;\
    }\
    .bokbo-filter-bar select {\
      appearance: none;\
      -webkit-appearance: none;\
      padding: 7px 30px 7px 12px;\
      font-size: 13px;\
      font-family: inherit;\
      color: #333;\
      background: #fff url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%23999\'/%3E%3C/svg%3E") no-repeat right 10px center;\
      border: 1px solid #ddd;\
      border-radius: 6px;\
      cursor: pointer;\
      min-width: 130px;\
      transition: border-color 0.15s;\
    }\
    .bokbo-filter-bar select:focus {\
      outline: none;\
      border-color: #D4390A;\
      box-shadow: 0 0 0 2px rgba(212, 57, 10, 0.15);\
    }\
    .bokbo-filter-bar select.bokbo-filter-active {\
      border-color: #D4390A;\
      color: #D4390A;\
      font-weight: 600;\
    }\
    .bokbo-filter-clear {\
      margin-left: auto;\
      padding: 7px 16px;\
      font-size: 13px;\
      font-weight: 600;\
      font-family: inherit;\
      color: #fff;\
      background: #D4390A;\
      border: none;\
      border-radius: 6px;\
      cursor: pointer;\
      transition: background 0.15s, opacity 0.15s;\
      white-space: nowrap;\
    }\
    .bokbo-filter-clear:hover {\
      background: #b5300a;\
    }\
    .bokbo-filter-clear:disabled {\
      opacity: 0.4;\
      cursor: default;\
    }\
    #bokbo-sort {\
      margin-left: auto;\
      font-weight: 500;\
    }\
    .bokbo-active-pills {\
      display: flex;\
      flex-wrap: wrap;\
      gap: 8px;\
      padding: 0 16px 12px;\
      margin-top: -8px;\
    }\
    .bokbo-pill {\
      display: inline-flex;\
      align-items: center;\
      gap: 6px;\
      padding: 5px 10px;\
      font-size: 12px;\
      font-family: inherit;\
      font-weight: 500;\
      color: #D4390A;\
      background: #fdf5f4;\
      border: 1px solid #f0d4cf;\
      border-radius: 20px;\
      cursor: default;\
      white-space: nowrap;\
      line-height: 1;\
    }\
    .bokbo-pill-x {\
      display: inline-flex;\
      align-items: center;\
      justify-content: center;\
      width: 16px;\
      height: 16px;\
      font-size: 14px;\
      font-weight: 700;\
      line-height: 1;\
      color: #D4390A;\
      background: transparent;\
      border: none;\
      border-radius: 50%;\
      cursor: pointer;\
      padding: 0;\
      transition: background 0.15s, color 0.15s;\
    }\
    .bokbo-pill-x:hover {\
      background: #D4390A;\
      color: #fff;\
    }\
    .bokbo-filter-count {\
      font-size: 12px;\
      color: #999;\
      white-space: nowrap;\
    }\
    @media (max-width: 768px) {\
      .bokbo-filter-bar {\
        gap: 8px;\
      }\
      .bokbo-filter-row {\
        gap: 8px;\
      }\
      .bokbo-filter-row select {\
        min-width: 0;\
        flex: 1 1 calc(50% - 8px);\
      }\
      #bokbo-sort {\
        margin-left: 0;\
        flex: 1 1 calc(50% - 8px);\
      }\
      .bokbo-filter-clear {\
        margin-left: 0;\
        flex: 1 1 100%;\
      }\
      .bokbo-filter-count {\
        flex: 1 1 100%;\
        text-align: center;\
      }\
    }\
    .bokbo-scroll-loader {\
      display: flex;\
      align-items: center;\
      justify-content: center;\
      gap: 10px;\
      padding: 32px 0 8px;\
      grid-column: 1 / -1;\
    }\
    .bokbo-scroll-loader-dot {\
      width: 8px;\
      height: 8px;\
      border-radius: 50%;\
      background: #ccc;\
      animation: bokbo-pulse 1.2s ease-in-out infinite;\
    }\
    .bokbo-scroll-loader-dot:nth-child(2) { animation-delay: 0.15s; }\
    .bokbo-scroll-loader-dot:nth-child(3) { animation-delay: 0.3s; }\
    @keyframes bokbo-pulse {\
      0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }\
      40% { opacity: 1; transform: scale(1); }\
    }\
    .bokbo-scroll-end {\
      text-align: center;\
      padding: 24px 0 0;\
      font-size: 13px;\
      color: #bbb;\
      grid-column: 1 / -1;\
    }\
    @keyframes bokbo-shimmer {\
      0% { background-position: -400px 0; }\
      100% { background-position: 400px 0; }\
    }\
    .bokbo-skeleton-card {\
      display: flex;\
      flex-direction: column;\
      background: #fff;\
      border: 1px solid #e8e8e8;\
      border-radius: 10px;\
      overflow: hidden;\
    }\
    .bokbo-skeleton-card-img {\
      width: 100%;\
      padding-top: 100%;\
      background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);\
      background-size: 800px 100%;\
      animation: bokbo-shimmer 1.4s ease-in-out infinite;\
    }\
    .bokbo-skeleton-card-body {\
      padding: 12px 14px 16px;\
      display: flex;\
      flex-direction: column;\
      gap: 8px;\
    }\
    .bokbo-skeleton-bar {\
      height: 12px;\
      border-radius: 4px;\
      background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);\
      background-size: 800px 100%;\
      animation: bokbo-shimmer 1.4s ease-in-out infinite;\
    }\
    .bokbo-skeleton-bar--category {\
      width: 35%;\
      height: 10px;\
    }\
    .bokbo-skeleton-bar--title {\
      width: 80%;\
    }\
    .bokbo-skeleton-bar--title2 {\
      width: 55%;\
    }\
    .bokbo-skeleton-bar--price {\
      width: 30%;\
      height: 16px;\
      margin-top: 4px;\
    }\
    .bokbo-skeleton-still {\
      text-align: center;\
      padding: 16px 0;\
      font-size: 13px;\
      color: #999;\
      grid-column: 1 / -1;\
    }\
  ';

  function injectStyles() {
    if (document.getElementById('bokbo-results-styles')) return;
    var style = document.createElement('style');
    style.id = 'bokbo-results-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  // ── Find and replace the native results container ───────────────
  //
  // Shopify themes use a variety of selectors for the search results
  // area. We try the most common ones, then fall back to injecting
  // before the page footer if nothing matches.
  //
  var NATIVE_SELECTORS = [
    '#MainContent',
    'main#main',
    'main[role="main"]',
    '.main-content',
    '#shopify-section-template--search',
    '.template-search',
    '.search-results',
    '#search-results',
    '[data-section-type="search"]',
    '.search-template-section',
  ];

  function findNativeContainer() {
    for (var i = 0; i < NATIVE_SELECTORS.length; i++) {
      var el = document.querySelector(NATIVE_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  // ── Filter config ────────────────────────────────────────────────

  var PRICE_RANGES = [
    { label: 'Under $50',    min: 0,      max: 50    },
    { label: '$50 – $100',   min: 50,     max: 100   },
    { label: '$100 – $250',  min: 100,    max: 250   },
    { label: '$250 – $500',  min: 250,    max: 500   },
    { label: 'Over $500',    min: 500,    max: Infinity },
  ];

  // Colors to extract from product title, product_type, and all_tags
  var COLOR_WORDS = [
    'Black', 'White', 'Grey', 'Gray', 'Silver', 'Gold', 'Pink', 'Red',
    'Blue', 'Green', 'Purple', 'Yellow', 'Orange', 'Brown', 'Beige',
    'Cream', 'Ivory', 'Navy', 'Nude', 'Champagne', 'Rose', 'Blush',
    'Burgundy', 'Wine', 'Coral', 'Teal', 'Mint', 'Lavender', 'Multicolor',
  ];
  var COLOR_REGEX = new RegExp('\\b(' + COLOR_WORDS.join('|') + ')\\b', 'i');

  // ── Size classification ──────────────────────────────────────────
  //
  // Size groups and their canonical display order.  Each group has a
  // regex that matches raw size strings from the `available_sizes`
  // payload field.  A raw size is classified into the FIRST group
  // whose regex matches.
  //

  var SIZE_GROUPS = [
    {
      key: 'clothing',
      label: 'Clothing',
      // XS, S, M, L, XL, XXL, XXXL (case-insensitive, standalone)
      regex: /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL)$/i,
      order: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'],
      normalize: function (raw) {
        var u = raw.toUpperCase();
        if (u === '2XL') return 'XXL';
        if (u === '3XL') return 'XXXL';
        return u;
      },
    },
    {
      key: 'plus',
      label: 'Plus',
      // 1X, 2X, 3X, 4X
      regex: /^([1-4]X)$/i,
      order: ['1X', '2X', '3X', '4X'],
      normalize: function (raw) { return raw.toUpperCase(); },
    },
    {
      key: 'numeric',
      label: 'Numeric',
      // 0, 00, 2, 4, 6, 8, 10, 12, 14, 16  (even numbers and 00)
      regex: /^(00|0|[2-9]|1[0-6])$/,
      order: ['00', '0', '2', '4', '6', '8', '10', '12', '14', '16'],
      normalize: function (raw) { return raw; },
    },
    {
      key: 'kids',
      label: 'Kids',
      // 2T, 3T, 4T, 5T, 6, 7, 8, 10, 12, 14  — or "Kids 6" etc.
      regex: /^(kids?\s*)?([2-5]T|[2-9]|1[0-4])$/i,
      order: ['2T', '3T', '4T', '5T', '6', '7', '8', '10', '12', '14'],
      normalize: function (raw) {
        var cleaned = raw.replace(/^kids?\s*/i, '').toUpperCase();
        return cleaned;
      },
    },
    {
      key: 'shoe',
      label: 'Shoes',
      // 5, 5.5, 6, 6.5 ... 13  — must contain a decimal OR be in 5-13 range
      regex: /^(\d{1,2}(?:\.5)?)$/,
      order: [],  // built dynamically
      normalize: function (raw) { return raw; },
      // Custom match: only classify as shoe if value is 5–13 (incl. halves)
      test: function (raw) {
        var n = parseFloat(raw);
        return !isNaN(n) && n >= 5 && n <= 13 && (n % 0.5 === 0);
      },
    },
  ];

  /**
   * Classify a single raw size string into { group, normalized }.
   * Returns null if no group matches.
   *
   * Optional second arg `context` lets the caller hint which family the
   * size belongs to — the SIZE_GROUPS regex list is ordered
   * clothing → plus → numeric → kids → shoe, so a bare whole number
   * like "11" matches `numeric` before it ever reaches the shoe group
   * and gets dropped in the clothing size dropdown (which only offers
   * 0–16 even). When the caller knows the product is footwear or the
   * applied filter is `shoe:N`, pass `context = 'shoe'` to promote the
   * shoe group above numeric for this one call.
   */
  function classifySize(raw, context) {
    var trimmed = (raw || '').trim();
    if (!trimmed) return null;

    // Shoe-context fast path: if the caller says this is footwear, try
    // the shoe group FIRST so whole numbers like 11 don't get stolen
    // by the numeric/clothing bucket. Falls through to the generic
    // cascade on no-match.
    if (context === 'shoe') {
      for (var si = 0; si < SIZE_GROUPS.length; si++) {
        if (SIZE_GROUPS[si].key !== 'shoe') continue;
        var shoeG = SIZE_GROUPS[si];
        if (shoeG.test ? shoeG.test(trimmed) : shoeG.regex.test(trimmed)) {
          return { group: 'shoe', normalized: shoeG.normalize(trimmed) };
        }
      }
    }

    for (var i = 0; i < SIZE_GROUPS.length; i++) {
      var g = SIZE_GROUPS[i];
      // Use custom test function if provided (shoe sizes need range check)
      if (g.test) {
        if (g.test(trimmed)) return { group: g.key, normalized: g.normalize(trimmed) };
      } else if (g.regex.test(trimmed)) {
        return { group: g.key, normalized: g.normalize(trimmed) };
      }
    }
    return null;
  }

  /**
   * Infer the size context ('shoe' | null) from a product's Shopify
   * product_type path ("Men > Shoes > Sneakers"). Used by
   * getProductSizes so that a Shoes/Sneakers product with sizes like
   * ["8","9","10","11"] gets classified into the shoe dropdown instead
   * of the clothing-numeric one.
   */
  function inferSizeContext(r) {
    var pt = (r && r.product_type) ? String(r.product_type).toLowerCase() : '';
    if (!pt) return null;
    if (/\b(shoe|shoes|sneaker|sneakers|boot|boots|sandal|sandals|heel|heels|loafer|loafers|mule|mules|wedge|wedges|pump|pumps|oxford|oxfords|slipper|slippers|clog|clogs|trainer|trainers|footwear)\b/.test(pt)) {
      return 'shoe';
    }
    return null;
  }

  /**
   * Auto-select the size dropdown + highlight matching size pills when
   * the server echoes an applied size in meta.applied_filters.
   *
   * The server returns `meta.applied_filters: ["color: Red", "size: 6"]`
   * on queries where it detected size via NLP ("red dress 6"). The
   * color half already has its own pre-selection path (via
   * detected_color on the response root); size doesn't have a
   * dedicated top-level field, so we parse applied_filters.
   *
   * No-op when:
   *   - meta.applied_filters is missing / non-array
   *   - no "size: X" entry found
   *   - the shopper has already chosen a size filter (don't stomp it)
   *   - the classified size doesn't map to any of the three size
   *     dropdowns (clothing / shoe / kids)
   *   - the resolved option value isn't present in the dropdown
   *     (very sparse result set — fall through silently rather than
   *     inject a synthetic option for a size the dropdown doesn't
   *     offer)
   */
  function syncSizeFilterFromMeta(data) {
    try {
      var meta = data && data.meta;
      var applied = meta && Array.isArray(meta.applied_filters) ? meta.applied_filters : null;
      if (!applied) return;
      var sizeEntry = null;
      for (var i = 0; i < applied.length; i++) {
        if (typeof applied[i] === 'string' && /^size\s*:/i.test(applied[i])) {
          sizeEntry = applied[i];
          break;
        }
      }
      if (!sizeEntry) return;

      // Shopper already picked a size — respect their choice.
      if (activeFilters.size_clothing || activeFilters.size_shoe || activeFilters.size_kids) return;

      var sizeValue = sizeEntry.replace(/^size\s*:\s*/i, '').trim();
      if (!sizeValue) return;

      // Server may send the size with an explicit group prefix
      // ("shoe:11", "clothing:M", "kids:7") when context disambiguates
      // which dropdown the shopper meant. Honor that prefix directly —
      // classifySize would otherwise bucket bare "11" as clothing-numeric
      // (since numeric matches before shoe in SIZE_GROUPS) and drop it
      // in the clothing dropdown, which only offers 0,2,4,…,16 and has
      // no "11" option to select at all.
      var classified;
      var prefixMatch = sizeValue.match(/^(shoe|clothing|kids|plus|numeric)\s*:\s*(.+)$/i);
      if (prefixMatch) {
        var forcedGroup = prefixMatch[1].toLowerCase();
        var rawAfterPrefix = prefixMatch[2].trim();
        // Find the group config so we can normalize consistently with
        // the dropdown's own option values (e.g. "M" vs "m", "2XL" vs "XXL").
        var groupCfg = null;
        for (var gi = 0; gi < SIZE_GROUPS.length; gi++) {
          if (SIZE_GROUPS[gi].key === forcedGroup) { groupCfg = SIZE_GROUPS[gi]; break; }
        }
        var normalized = groupCfg && typeof groupCfg.normalize === 'function'
          ? groupCfg.normalize(rawAfterPrefix) : rawAfterPrefix;
        classified = { group: forcedGroup, normalized: normalized };
      } else {
        classified = classifySize(sizeValue);
      }
      if (!classified) return;

      var dropdownKey = SIZE_DROPDOWN_MAP[classified.group];
      if (!dropdownKey) return;

      var optionValue = classified.group + ':' + classified.normalized;
      var sel = document.getElementById('bokbo-filter-' + dropdownKey);
      if (!sel) return;
      if (!sel.querySelector('option[value="' + CSS.escape(optionValue) + '"]')) return;

      activeFilters[dropdownKey] = optionValue;
      sel.value = optionValue;
      sel.classList.add('bokbo-filter-active');
      updateFilterPills();
      var clearBtn = document.getElementById('bokbo-filter-clear');
      if (clearBtn) clearBtn.disabled = false;
      console.log('[BokBo Results] Size detected in query:', sizeValue, '— pre-selected', dropdownKey, '=', optionValue);
    } catch (_) { /* never break render on UI sync */ }
  }

  /**
   * Pre-select the Gender dropdown when the server echoes a
   * "category: Men/Women/Kids" entry in meta.applied_filters — i.e. the
   * NLP extractor pulled the gender out of the raw query ("mens sneakers
   * 11", "womens heels 7") and we want the Level-1 cascade to reflect
   * that on first render.
   *
   * Same behavior contract as syncSizeFilterFromMeta:
   *   - No-op if the shopper already selected a gender (their choice wins).
   *   - No-op if the dropdown doesn't have the matching option value
   *     (stale option list, plural vs. singular, etc.).
   *   - Never throws — render path must not break on UI sync.
   */
  function syncCategoryFilterFromMeta(data) {
    try {
      var meta = data && data.meta;
      var applied = meta && Array.isArray(meta.applied_filters) ? meta.applied_filters : null;
      if (!applied) return;
      var catEntry = null;
      for (var i = 0; i < applied.length; i++) {
        if (typeof applied[i] === 'string' && /^category\s*:/i.test(applied[i])) {
          catEntry = applied[i];
          break;
        }
      }
      if (!catEntry) return;

      // Shopper already picked a gender — respect their choice.
      if (activeFilters.category) return;

      var catValue = catEntry.replace(/^category\s*:\s*/i, '').trim();
      if (!catValue) return;

      var sel = document.getElementById('bokbo-filter-category');
      if (!sel) return;
      if (!sel.querySelector('option[value="' + CSS.escape(catValue) + '"]')) return;

      activeFilters.category = catValue;
      sel.value = catValue;
      sel.classList.add('bokbo-filter-active');
      updateFilterPills();
      var clearBtn = document.getElementById('bokbo-filter-clear');
      if (clearBtn) clearBtn.disabled = false;
      console.log('[BokBo Results] Gender detected in query:', catValue, '— pre-selected category dropdown');
    } catch (_) { /* never break render on UI sync */ }
  }

  /**
   * Get all classified sizes for a product from its available_sizes array.
   * Returns an array of { group, normalized } objects.
   */
  function getProductSizes(r) {
    var sizes = r.available_sizes;
    if (!sizes || !Array.isArray(sizes) || sizes.length === 0) return [];

    // Footwear product_type promotes the shoe group above numeric so
    // whole-number sizes like "11" land in the shoe dropdown. Clothing
    // products keep the default ordering.
    var ctx = inferSizeContext(r);

    var result = [];
    var seen = {};
    for (var i = 0; i < sizes.length; i++) {
      var classified = classifySize(sizes[i], ctx);
      if (classified && !seen[classified.group + ':' + classified.normalized]) {
        seen[classified.group + ':' + classified.normalized] = true;
        result.push(classified);
      }
    }
    return result;
  }

  /**
   * Check whether a product has a specific size (group:normalized).
   */
  function productHasSize(r, sizeValue) {
    var parts = sizeValue.split(':');
    if (parts.length !== 2) return false;
    var targetGroup = parts[0];
    var targetNorm = parts[1];
    var pSizes = getProductSizes(r);
    for (var i = 0; i < pSizes.length; i++) {
      if (pSizes[i].group === targetGroup && pSizes[i].normalized === targetNorm) return true;
    }
    return false;
  }

  // Which SIZE_GROUPS belong to which dropdown
  var SIZE_DROPDOWN_MAP = {
    clothing: 'size_clothing',
    plus:     'size_clothing',
    numeric:  'size_clothing',
    kids:     'size_kids',
    shoe:     'size_shoe',
  };

  /**
   * Build the grouped size options from allResults, split by dropdown type.
   * Returns { size_clothing: [...], size_shoe: [...], size_kids: [...] }
   * Each array contains { value: 'group:normalized', label: 'Display' }.
   */
  function buildSizeOptions() {
    // Collect unique group:normalized combos across all results
    var found = {};  // key = 'group:normalized', value = true

    for (var i = 0; i < allResults.length; i++) {
      var pSizes = getProductSizes(allResults[i]);
      for (var j = 0; j < pSizes.length; j++) {
        found[pSizes[j].group + ':' + pSizes[j].normalized] = true;
      }
    }

    // Build options per dropdown type
    var result = { size_clothing: [], size_shoe: [], size_kids: [] };

    for (var g = 0; g < SIZE_GROUPS.length; g++) {
      var group = SIZE_GROUPS[g];
      var dropdownKey = SIZE_DROPDOWN_MAP[group.key];
      if (!dropdownKey) continue;

      var groupSizes = [];
      var keys = Object.keys(found);
      for (var k = 0; k < keys.length; k++) {
        var parts = keys[k].split(':');
        if (parts[0] === group.key) {
          groupSizes.push(parts[1]);
        }
      }

      if (groupSizes.length === 0) continue;

      // Sort by defined order if available, else numerically
      if (group.order && group.order.length > 0) {
        groupSizes.sort(function (a, b) {
          var ai = group.order.indexOf(a);
          var bi = group.order.indexOf(b);
          if (ai === -1) ai = 999;
          if (bi === -1) bi = 999;
          return ai - bi;
        });
      } else {
        groupSizes.sort(function (a, b) { return parseFloat(a) - parseFloat(b); });
      }

      for (var s = 0; s < groupSizes.length; s++) {
        result[dropdownKey].push({
          value: group.key + ':' + groupSizes[s],
          label: groupSizes[s],
        });
      }
    }

    return result;
  }

  // ── Color-family normalization (mirror of api/server.js COLOR_FAMILIES) ─
  //
  // The server now normalizes detected_color at both index time AND at
  // response time, so most payloads arrive already bucketed into one of
  // the 12 families. This client-side map is the belt-and-suspenders
  // fallback: (a) shoppers on an older widget build vs. newer server
  // deploy, (b) cached responses emitted before the server upgrade, and
  // (c) direct hits on whatever legacy "burgundy"/"slate" shades slipped
  // through. Any color the widget can't classify is dropped from the
  // facet dropdown rather than cluttering it with 400 merchant colorways.
  var BOKBO_COLOR_FAMILIES = {
    red:    ['red','crimson','scarlet','burgundy','wine','merlot','cherry','rose','coral','tomato','ruby','garnet','maroon','oxblood','brick','rust'],
    pink:   ['pink','blush','mauve','fuchsia','magenta','hot pink','bubblegum','flamingo','salmon','peach','ballet','dusty rose','rose gold'],
    orange: ['orange','tangerine','apricot','amber','burnt orange','terracotta','clay','sienna','paprika'],
    yellow: ['yellow','gold','mustard','lemon','butter','canary','sunshine','goldenrod','honey','champagne'],
    green:  ['green','olive','sage','forest','hunter','mint','emerald','lime','army','khaki','moss','jade','teal','seafoam','pistachio'],
    blue:   ['blue','navy','cobalt','royal','sky','baby blue','denim','indigo','cornflower','cerulean','powder blue','slate','steel blue'],
    purple: ['purple','lavender','lilac','violet','plum','grape','orchid','eggplant','mauve','periwinkle','wisteria'],
    brown:  ['brown','tan','camel','caramel','chocolate','coffee','mocha','taupe','beige','sand','nude','latte','cognac','saddle','chestnut'],
    white:  ['white','ivory','cream','off white','ecru','snow','vanilla','pearl','eggshell','linen'],
    black:  ['black','onyx','jet','charcoal','ebony','midnight','noir','carbon','graphite'],
    gray:   ['gray','grey','silver','slate','ash','dove','stone','pewter','heather','smoke'],
    multi:  ['multi','multicolor','print','pattern','floral','tie dye','rainbow','colorblock'],
  };
  var BOKBO_COLOR_TO_FAMILY = (function () {
    var map = {};
    var families = Object.keys(BOKBO_COLOR_FAMILIES);
    for (var fi = 0; fi < families.length; fi++) {
      var family = families[fi];
      var shades = BOKBO_COLOR_FAMILIES[family];
      for (var si = 0; si < shades.length; si++) {
        map[shades[si].toLowerCase()] = family;
      }
    }
    return map;
  })();
  function bokboNormalizeColor(raw) {
    if (!raw) return null;
    var lower = String(raw).toLowerCase().trim();
    if (!lower) return null;
    if (BOKBO_COLOR_TO_FAMILY[lower]) return BOKBO_COLOR_TO_FAMILY[lower];
    var keys = Object.keys(BOKBO_COLOR_TO_FAMILY);
    for (var i = 0; i < keys.length; i++) {
      if (lower.indexOf(keys[i]) !== -1) return BOKBO_COLOR_TO_FAMILY[keys[i]];
    }
    return null;
  }
  var BOKBO_FAMILY_LABELS = {
    red: 'Red', pink: 'Pink', orange: 'Orange', yellow: 'Yellow',
    green: 'Green', blue: 'Blue', purple: 'Purple', brown: 'Brown',
    white: 'White', black: 'Black', gray: 'Gray', multi: 'Multi',
  };

  /** Extract color FAMILY label from AI-detected field. Returns '' if
   *  not set OR the shade can't be bucketed into one of the 12 families
   *  (so the dropdown stays clean even with legacy unnormalized data). */
  function extractColor(r) {
    if (!r || !r.detected_color) return '';
    var family = bokboNormalizeColor(r.detected_color);
    if (family && BOKBO_FAMILY_LABELS[family]) return BOKBO_FAMILY_LABELS[family];
    // Server-normalized already? Accept the value as-is if it's one of
    // the canonical family keys. Falls through to '' for anything else.
    var lower = String(r.detected_color).toLowerCase().trim();
    if (BOKBO_FAMILY_LABELS[lower]) return BOKBO_FAMILY_LABELS[lower];
    return '';
  }

  // ── Sort options ─────────────────────────────────────────────
  var SORT_OPTIONS = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'newest', label: 'Newest' },
  ];
  var SORT_STORAGE_KEY = 'bokbo_sort_preference';

  /** Read saved sort preference, default to 'relevance'. */
  function loadSortPreference() {
    try {
      var saved = localStorage.getItem(SORT_STORAGE_KEY);
      if (saved && SORT_OPTIONS.some(function (o) { return o.value === saved; })) return saved;
    } catch (e) { /* localStorage unavailable */ }
    return 'relevance';
  }

  /** Save sort preference to localStorage. */
  function saveSortPreference(value) {
    try { localStorage.setItem(SORT_STORAGE_KEY, value); } catch (e) { /* ignore */ }
  }

  var activeSort = loadSortPreference();

  // ── Filter state ───────────────────────────────────────────────
  // ── Cascading filter state ──────────────────────────────────────
  // Level 1: gender (Women, Men, Kids)
  // Level 2: product_type (Dresses, Tops, Shoes, …)
  // Level 3: subcategory (Maxi, Mini, Sneakers, …)
  // Plus independent: price, color, brand, size_clothing, size_shoe, size_kids
  var activeFilters = {
    category: '',          // Level 1 — gender (sent to API as 'category')
    product_type: '',      // Level 2 — product type segment
    subcategory: '',       // Level 3 — subcategory segment
    price: '', color: '', brand: '',
    size_clothing: '', size_shoe: '', size_kids: ''
  };
  var filteredResults = [];  // subset of allResults after filters
  // Tracks whether the user has explicitly dismissed the NLP-detected color
  // filter. When true, outgoing /search requests include `noColorDetect:true`
  // so the server won't re-run color extraction on the raw query and silently
  // reinstate the filter the user just removed.
  var userClearedColor = false;
  // Mirror flag for brand. When the shopper resets the brand dropdown to
  // "All Brands" (or dismisses the brand pill), the query text on the
  // page itself hasn't changed — server-side NLP would happily detect
  // "Nike" as a brand again and re-enter brand mode, which feels to the
  // user like "nothing happened" (the dropdown shows All, but all
  // results are still Nike). Sending noBrandDetect=true on subsequent
  // requests tells the server to skip brand detection for the rest of
  // this session, so clearing the filter actually expands results.
  var userClearedBrand = false;

  // Set true whenever the server response reports a size filter in
  // meta.applied_filters (e.g. ["size: shoe:11", "category: Men"] for
  // a "mens sneakers 11" query). When true, applyFilters MUST NOT
  // apply any client-side size-based filter, sort, or tier re-shuffle
  // — the server's applySizeSort has already partitioned results into
  // exact → close → other tiers using the full shoe alias set (bare,
  // US prefix, gender suffixes, EU/UK conversions), which the widget
  // classifier can't reproduce. Any client-side size reorder here
  // would scramble that order and promote wrong-size products.
  //
  // Cleared whenever a fresh response arrives without a size entry in
  // applied_filters (i.e. server did not apply a size filter), so the
  // flag never sticks across navigation into a non-size-filtered
  // query.
  var serverHandledSize = false;

  // Product types that indicate footwear (show Shoe Size instead of Clothing Size)
  var FOOTWEAR_TYPES = { 'Shoes': true, 'Boots': true, 'Sneakers': true, 'Sandals': true };

  /**
   * Inspect meta.applied_filters for a "size: …" entry and update the
   * serverHandledSize flag accordingly. Called from every XHR callback
   * (initial paint, refine-input re-search, brand follow-up, filter
   * change) so the flag always reflects the response currently on
   * screen. Returns true/false so callers can log the transition.
   */
  function updateServerHandledSizeFromMeta(data) {
    try {
      var applied = data && data.meta && data.meta.applied_filters;
      if (!Array.isArray(applied)) {
        serverHandledSize = false;
        console.log('[BokBo] STEP 2 — serverHandledSize:', serverHandledSize,
          'filters:', data && data.meta && data.meta.applied_filters);
        return false;
      }
      for (var i = 0; i < applied.length; i++) {
        if (typeof applied[i] === 'string' && /^size\s*:/i.test(applied[i])) {
          serverHandledSize = true;
          console.log('[BokBo] STEP 2 — serverHandledSize:', serverHandledSize,
            'filters:', data && data.meta && data.meta.applied_filters);
          return true;
        }
      }
      serverHandledSize = false;
      console.log('[BokBo] STEP 2 — serverHandledSize:', serverHandledSize,
        'filters:', data && data.meta && data.meta.applied_filters);
      return false;
    } catch (_) {
      serverHandledSize = false;
      console.log('[BokBo] STEP 2 — serverHandledSize:', serverHandledSize,
        '(caught exception reading applied_filters)');
      return false;
    }
  }

  /** Parse product_type string "Gender > Type > Sub" into { gender, type, sub }. */
  function parseProductType(pt) {
    if (!pt) return { gender: '', type: '', sub: '' };
    var parts = pt.split('>');
    return {
      gender: (parts[0] || '').trim(),
      type:   (parts[1] || '').trim(),
      sub:    (parts[2] || '').trim()
    };
  }

  /**
   * Build the unique sorted option lists by scanning the current result set.
   * Returns gender, product types, subcategories, colors, brands, and sizes.
   * Product types and subcategories are scoped to the active gender/type selections.
   */
  function buildFilterOptions() {
    var genders = {}, types = {}, subs = {}, colors = {}, brands = {};
    var source = allResults;

    for (var i = 0; i < source.length; i++) {
      var r = source[i];
      var parsed = parseProductType(r.product_type);

      if (parsed.gender) genders[parsed.gender] = true;

      // Only show product types matching selected gender
      if (!activeFilters.category || parsed.gender === activeFilters.category) {
        if (parsed.type) types[parsed.type] = true;
      }

      // Only show subcategories matching selected gender + product type
      if ((!activeFilters.category || parsed.gender === activeFilters.category) &&
          (!activeFilters.product_type || parsed.type === activeFilters.product_type)) {
        if (parsed.sub) subs[parsed.sub] = true;
      }

      var c = extractColor(r);
      if (c) colors[c] = true;
      if (r.vendor) brands[r.vendor] = true;
    }

    var sizes = buildSizeOptions();
    return {
      genders: Object.keys(genders).sort(),
      types: Object.keys(types).sort(),
      subcategories: Object.keys(subs).sort(),
      colors: Object.keys(colors).sort(),
      brands: Object.keys(brands).sort(),
      size_clothing: sizes.size_clothing,
      size_shoe: sizes.size_shoe,
      size_kids: sizes.size_kids,
    };
  }

  /**
   * Score how well a product title matches the search query.
   * Returns a number 0–1 representing the fraction of query words found
   * as whole words in the title (case-insensitive).
   */
  function queryRelevanceScore(r) {
    if (!query) return 0;
    var words = query.toLowerCase().split(/\s+/).filter(function (w) { return w.length >= 2; });
    if (words.length === 0) return 0;
    var title = (r.title || '').toLowerCase();
    var matches = 0;
    for (var i = 0; i < words.length; i++) {
      // Whole-word match using word boundary trick
      var re = new RegExp('\\b' + words[i].replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
      if (re.test(title)) matches++;
    }
    return matches / words.length;
  }

  /**
   * Copy allResults → filteredResults, then apply the shopper's chosen
   * sort. No client-side size sort: the server-side applySizeSort has
   * already hoisted exact / close matches to the top, and we preserve
   * that tier order intact here. The ONLY cases that reorder:
   *   - shopper explicitly picked price_asc / price_desc / newest
   *   - activeSort is relevance AND a brand filter is active (title
   *     relevance boost within the brand set, per-tier stable)
   * Under plain relevance with no brand filter, server order is
   * preserved BIT-FOR-BIT. Any future "helpful" client-side sort added
   * here for size_shoe / size_clothing / size_kids would undo the
   * server's exact→close→other tiers and scramble the shoe-size fix.
   */
  function applyFilters() {
    console.log('[BokBo] STEP 3 — applyFilters called, serverHandledSize:', serverHandledSize,
      'activeSort:', activeSort);
    filteredResults = allResults.slice();

    // When the server has already applied a size filter (meta.applied_filters
    // contained "size: …"), it has also run applySizeSort — which partitions
    // results into exact → close → other tiers using the full alias set that
    // the widget's classifier can't reproduce. Trust that order completely.
    // The ONLY shopper-initiated override we still honor here is an explicit
    // price / newest pick; anything else (relevance, brand-relevance boost,
    // or any future helpful "client-side size assist") would scramble the
    // tiered order. Log the short-circuit loudly so future regressions are
    // visible in the console without having to re-instrument.
    if (serverHandledSize && activeSort === 'relevance') {
      console.log('[BokBo] STEP 4 — early return firing, top 3:',
        filteredResults.slice(0, 3).map(function (r) { return r.title; }));
      console.log('[BokBo Results] serverHandledSize=true + relevance sort — preserving server order, NO client-side sort');
      try {
        console.log('[BokBo] post-applyFilters order (top 5, server-handled):');
        filteredResults.slice(0, 5).forEach(function (r, i) {
          console.log('  ' + (i + 1) + '. ' + (r.title || '(no title)') +
            ' — sizes: ' + ((r.available_sizes || []).join(', ') || '(none)'));
        });
      } catch (_) {}
      return;
    }

    if (activeSort === 'price_asc') {
      filteredResults.sort(function (a, b) { return (a.price || 0) - (b.price || 0); });
      console.log('[BokBo Results] Sorted by price_asc —', filteredResults.length, 'products');
    } else if (activeSort === 'price_desc') {
      filteredResults.sort(function (a, b) { return (b.price || 0) - (a.price || 0); });
      console.log('[BokBo Results] Sorted by price_desc —', filteredResults.length, 'products');
    } else if (activeSort === 'newest') {
      filteredResults.sort(function (a, b) {
        var da = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        var db = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return db - da;
      });
      console.log('[BokBo Results] Sorted by newest —', filteredResults.length, 'products');
    } else if (activeSort === 'relevance' && activeFilters.brand) {
      // Brand + relevance: title-match products within the brand set
      // get hoisted above brand-only matches. Stable within each tier.
      filteredResults.sort(function (a, b) {
        var sa = queryRelevanceScore(a);
        var sb = queryRelevanceScore(b);
        if (sa !== sb) return sb - sa;
        var scoreA = a.score != null ? a.score : -1;
        var scoreB = b.score != null ? b.score : -1;
        return scoreB - scoreA;
      });
      console.log('[BokBo Results] Sorted by relevance + brand-boost —', filteredResults.length, 'products');
    } else {
      // Default: preserve server order. NO client-side sort. The
      // server's applySizeSort has already placed exact-size products
      // first, close-size (±0.5) second, others last — re-sorting
      // here would scramble that.
      console.log('[BokBo Results] Preserving server order (relevance, no brand) —', filteredResults.length, 'products');
    }

    // Step-2 diagnostic — confirm that applyFilters didn't scramble the
    // server-side size-first order. When activeSort === 'relevance' and
    // there's no brand filter, we just slice allResults, so the order
    // here MUST match the API response. If it doesn't, a re-sort slipped
    // in somewhere above. The brand-relevance branch is documented as
    // preserving server score order within each relevance tier, but
    // it IS a sort — watch for "red dress 6" + brand filter combos.
    try {
      console.log('[BokBo] post-applyFilters order (top 5):');
      filteredResults.slice(0, 5).forEach(function (r, i) {
        console.log('  ' + (i + 1) + '. ' + (r.title || '(no title)') +
          ' — sizes: ' + ((r.available_sizes || []).join(', ') || '(none)'));
      });
    } catch (_) {}
  }

  /** Check whether any filter is active. */
  function hasActiveFilters() {
    return !!(activeFilters.category || activeFilters.product_type || activeFilters.subcategory ||
              activeFilters.price || activeFilters.color ||
              activeFilters.size_clothing || activeFilters.size_shoe || activeFilters.size_kids ||
              activeFilters.brand);
  }

  // ── Infinite-scroll state ────────────────────────────────────────
  var allResults = [];     // full result set from API (up to 200)
  var shownCount = 0;      // how many cards are currently in the DOM
  var isAppending = false;  // guard against overlapping appends
  var container = null;    // outer wrapper — set in init()
  var grid = null;         // the .bokbo-results-grid element

  // ── Render functions ────────────────────────────────────────────

  function renderContainer() {
    var el = document.createElement('div');
    el.className = 'bokbo-results-container';
    el.id = 'bokbo-results';
    return el;
  }

  var resultsStillSearchingTimer = null;

  function buildSkeletonCardHtml() {
    return '<div class="bokbo-skeleton-card">' +
      '<div class="bokbo-skeleton-card-img"></div>' +
      '<div class="bokbo-skeleton-card-body">' +
        '<div class="bokbo-skeleton-bar bokbo-skeleton-bar--category"></div>' +
        '<div class="bokbo-skeleton-bar bokbo-skeleton-bar--title"></div>' +
        '<div class="bokbo-skeleton-bar bokbo-skeleton-bar--title2"></div>' +
        '<div class="bokbo-skeleton-bar bokbo-skeleton-bar--price"></div>' +
      '</div>' +
    '</div>';
  }

  function renderLoading() {
    // Skeleton is gated by two independent sources because settings
    // arrive via two paths (see applySettings):
    //   1. BOKBO.skeletonLoading — set by applySettings after the XHR
    //      fallback resolves.
    //   2. window.__BOKBO_SETTINGS.skeletonLoading — set synchronously
    //      by the app-proxy /apps/bokbo/settings.js snippet.
    // Either source evaluating to `false` suppresses the skeleton —
    // otherwise the widget could paint a skeleton while the XHR is
    // mid-flight, then the eventual settings ack never un-paints it.
    var proxy = window.__BOKBO_SETTINGS || null;
    var skeletonOn = BOKBO.skeletonLoading !== false &&
                     (!proxy || proxy.skeletonLoading !== false);
    if (!skeletonOn) {
      container.innerHTML =
        '<div class="bokbo-results-header">' +
          '<h1>Searching for \u201C' + escapeHtml(query) + '\u201D\u2026</h1>' +
          '<p id="bokbo-results-still" style="display:none;">Still searching\u2026</p>' +
        '</div>';
      clearTimeout(resultsStillSearchingTimer);
      resultsStillSearchingTimer = setTimeout(function () {
        var msg = document.getElementById('bokbo-results-still');
        if (msg) msg.style.display = 'block';
      }, 3000);
      return;
    }

    var skeletonGrid = '';
    for (var i = 0; i < 8; i++) {
      skeletonGrid += buildSkeletonCardHtml();
    }

    container.innerHTML =
      '<div class="bokbo-results-header">' +
        '<h1>Searching for \u201C' + escapeHtml(query) + '\u201D\u2026</h1>' +
      '</div>' +
      '<div class="bokbo-results-grid">' +
        skeletonGrid +
        '<div class="bokbo-skeleton-still" id="bokbo-results-still" style="display:none;">Still searching\u2026</div>' +
      '</div>';

    // Show "Still searching..." after 3 seconds
    clearTimeout(resultsStillSearchingTimer);
    resultsStillSearchingTimer = setTimeout(function () {
      var msg = document.getElementById('bokbo-results-still');
      if (msg) msg.style.display = 'block';
    }, 3000);
  }

  function renderNoResults() {
    container.innerHTML =
      '<div class="bokbo-results-empty">' +
        '<h2>No results found for \u201C' + escapeHtml(query) + '\u201D</h2>' +
        '<p>Try different keywords or check for typos.</p>' +
        '<a class="bokbo-search-again" href="/search">Search again</a>' +
      '</div>';
  }

  /**
   * Handle a size-specific no-results response from the API.
   *
   * When the shopper filters to a size that has zero inventory, the
   * server now short-circuits the relaxation cascade and returns
   * results:[] + meta.no_results_reason. Rendering the generic
   * "no products match" message in that case is misleading — the
   * shopper just asked for size 33 and we drowned them in size-M
   * leftovers last time. Instead we render a dedicated empty state
   * that names the size and offers a "Clear size filter" action.
   *
   * Renders into #bokbo-grid when the filter bar is already built (ie.
   * this was triggered from a filter change / pill click), otherwise
   * at the container level (initial page load).
   *
   * Returns true iff this helper handled the response — callers should
   * fall through to their existing no-results rendering when false.
   */
  function handleNoResultsReason(data) {
    var reason = data && data.meta && data.meta.no_results_reason;
    if (!reason) return false;

    // Today the server only emits this for the strict-size path. Keep
    // the detection permissive so future reasons (stock:, brand:, …)
    // still fall through cleanly — we'll add specific UIs as they land.
    var isSizeReason = /size/i.test(reason);
    if (!isSizeReason) return false;

    // Extract the size label the server baked into the reason string
    // ("no products found in size 33" → "33"). Fall back to "that size"
    // if the format ever changes so we never render a broken message.
    var sizeLabel = (reason.match(/size\s+(\S+)/i) || [])[1] || 'that size';
    var categoryLabel = (
      activeFilters.product_type ||
      activeFilters.category ||
      'products'
    ).toString().toLowerCase();

    var html =
      '<div class="bokbo-results-size-empty">' +
        '<h2>No ' + escapeHtml(categoryLabel) + ' found in size ' + escapeHtml(sizeLabel) + '.</h2>' +
        '<p>Try a different size or broaden your search.</p>' +
        '<button class="bokbo-clear-size-btn" type="button">Clear size filter</button>' +
      '</div>';

    var gridEl = document.getElementById('bokbo-grid');
    if (gridEl) {
      gridEl.innerHTML = html;
    } else if (container) {
      container.innerHTML = html;
    }

    // Wire the clear-size button. The two code paths diverge because
    // the filter bar may or may not exist: if it does, we can do a
    // programmatic re-search; if not (initial load path), we strip
    // size params from the URL and reload. Either way, size disappears.
    var clearBtn = document.querySelector('.bokbo-clear-size-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        activeFilters.size_clothing = '';
        activeFilters.size_shoe = '';
        activeFilters.size_kids = '';

        // Strip size-related URL params so refresh / share links don't
        // bring the filter back.
        try {
          var u = new URL(window.location.href);
          u.searchParams.delete('size_clothing');
          u.searchParams.delete('size_shoe');
          u.searchParams.delete('size_kids');
          u.searchParams.delete('size');
          window.history.replaceState(null, '', u.toString());
        } catch (_) {}

        if (document.getElementById('bokbo-filter-bar')) {
          rebuildFilterBar();
          onFiltersChanged();
        } else {
          // Initial-load path — no bar to rebuild. A reload re-enters
          // the normal init flow without size filters.
          window.location.reload();
        }
      });
    }

    return true;
  }

  /**
   * Show a non-blocking "Showing all sizes — no results in size M"
   * notice above the grid when the server relaxed the size filter.
   *
   * Triggered by meta.relaxed_filters containing a "size: …" entry.
   * The server drops the size filter when the filter's first pass
   * would have returned fewer than SIZE_RELAX_THRESHOLD results — we
   * surface that server-side decision to the shopper so the
   * now-visible non-matching sizes don't feel like a bug.
   *
   * Idempotent: removes any prior notice before rendering. Dismissing
   * via the × doesn't change state (the filter was already relaxed on
   * the server); it's a "got it" acknowledgment.
   */
  function renderSizeRelaxationNotice(data) {
    var existing = document.querySelector('.bokbo-relax-notice');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var relaxed = data && data.meta && data.meta.relaxed_filters;
    if (!Array.isArray(relaxed) || !relaxed.length) return;

    var sizeEntry = null;
    for (var i = 0; i < relaxed.length; i++) {
      if (typeof relaxed[i] === 'string' && /^size\s*:/i.test(relaxed[i])) {
        sizeEntry = relaxed[i];
        break;
      }
    }
    if (!sizeEntry) return;

    var sizeLabel = sizeEntry.replace(/^size\s*:\s*/i, '').trim() || 'that size';

    var notice = document.createElement('div');
    notice.className = 'bokbo-relax-notice';
    notice.innerHTML =
      '<span class="bokbo-relax-notice-msg">Showing all sizes \u2014 no results found in size ' +
        escapeHtml(sizeLabel) +
      '.</span>' +
      '<button class="bokbo-relax-notice-close" type="button" aria-label="Dismiss">\u00d7</button>';

    // Insert above the grid if one exists; otherwise append to the
    // container (initial-load path before renderShell has built it).
    if (grid && grid.parentNode) {
      grid.parentNode.insertBefore(notice, grid);
    } else if (container) {
      container.appendChild(notice);
    }

    var closeBtn = notice.querySelector('.bokbo-relax-notice-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (notice.parentNode) notice.parentNode.removeChild(notice);
      });
    }
  }

  function renderError() {
    container.innerHTML =
      '<div class="bokbo-results-error">' +
        'Something went wrong. Please try your search again.' +
      '</div>';
  }

  function buildCardHtml(r) {
    var imgSrc = r.image_url || '';
    var imgHtml = imgSrc
      ? '<div class="bokbo-results-card-img-wrap"><img class="bokbo-results-card-img" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(r.title || '') + '" loading="lazy"></div>'
      : '<div class="bokbo-results-card-img-wrap"><div class="bokbo-results-card-img-placeholder">\u2697</div></div>';

    // Brand name — sits at the top of the card body where the category
    // breadcrumb ("Women's > Dresses > Midi") used to live. The old
    // breadcrumb rendering was removed entirely per design feedback;
    // the filter bar + product title already communicate the category,
    // so the breadcrumb was redundant and visually noisy.
    //
    // Emitted as a <div role="link"> rather than an <a> because the
    // entire card is already wrapped in an <a class="bokbo-results-card">
    // and nested interactive elements break accessibility + trigger
    // browser warnings. The brand-click delegate below stops propagation
    // + prevents default so the outer card link doesn't also fire.
    // Rendered only when product.vendor is non-empty — otherwise nothing
    // takes the breadcrumb's old slot.
    var brandHtml = r.vendor
      ? '<div class="bokbo-card-brand" data-brand="' + escapeHtml(r.vendor) + '" role="link" tabindex="0">' + escapeHtml(r.vendor) + '</div>'
      : '';

    var priceHtml = formatPrice(r.price)
      ? '<div class="bokbo-results-card-price">' + formatPrice(r.price) + '</div>'
      : '';

    // Size pills — up to 6 shown, plus a "+more" chip when a product
    // has more variants than fit. When the current search has an
    // active size filter, the matching pill gets .bokbo-size-active
    // so the shopper sees AT A GLANCE that this card carries their
    // size (purple highlight matches the brand accent used elsewhere
    // in BokBo). Addresses the "clicked a dress and it was XS only"
    // conversion bug — the signal is visible pre-click now.
    var availableSizes = Array.isArray(r.available_sizes) ? r.available_sizes : [];
    var sizesHtml = '';
    if (availableSizes.length) {
      var shown = availableSizes.slice(0, 6);
      var pillsHtml = '';
      for (var sIdx = 0; sIdx < shown.length; sIdx++) {
        var sz = shown[sIdx];
        var cls = 'bokbo-size-pill' + (isActiveSize(sz) ? ' bokbo-size-active' : '');
        pillsHtml += '<span class="' + cls + '">' + escapeHtml(String(sz)) + '</span>';
      }
      var moreHtml = availableSizes.length > 6
        ? '<span class="bokbo-size-more">+more</span>'
        : '';
      sizesHtml = '<div class="bokbo-card-sizes">' + pillsHtml + moreHtml + '</div>';
    }

    // Fix 2 — "Other sizes available" note. When the shopper has an
    // active size filter AND this card doesn't actually stock that
    // size (it survived via the relaxation cascade), annotate the
    // card so the shopper knows why they're seeing it. Check goes
    // through getActiveSizeRaw() which normalizes the "clothing:M"
    // prefix shape the size dropdowns emit; availableSizes membership
    // is case-insensitive since merchants store size tags with
    // wildly inconsistent casing ("M" / "m" / "Medium").
    var activeSizeRaw = getActiveSizeRaw();
    var sizeNoteHtml = '';
    if (activeSizeRaw) {
      var activeLower = String(activeSizeRaw).toLowerCase();
      var hasRequestedSize = availableSizes.some(function (s) {
        return String(s || '').toLowerCase() === activeLower;
      });
      if (!hasRequestedSize) {
        sizeNoteHtml = '<div class="bokbo-size-note">Other sizes available</div>';
      }
    }

    // Native BokBo wishlist heart. Replaces the prior XB Wishlist
    // integration end-to-end: state lives in localStorage (with an
    // optional sync to /wishlist/:shareId for shareable links), click
    // is handled by a body-level delegate that toggles the heart +
    // shows a toast, and the filled/empty paint state is controlled
    // purely by the `.bokbo-heart-active` class that updateHeartButtons()
    // reconciles after every render.
    //
    // Gating rules:
    //   1. Per-shop toggle — the admin dashboard can disable the
    //      feature via settings.wishlistEnabled; the client reads that
    //      through isWishlistEnabled(), which honors both the BOKBO
    //      config (XHR fallback path) and window.__BOKBO_SETTINGS
    //      (app-proxy fast path).
    //   2. Product identity — we need a stable shopify_id to dedupe
    //      items in the wishlist, and handle to rebuild links later.
    //      Skip rendering when either is missing rather than render
    //      a button that can't round-trip.
    var productId = r.shopify_id || '';
    var productHandle = r.handle || '';
    var wishlistBtn = '';
    if (isWishlistEnabled() && productId && productHandle) {
      var wishlistAriaLabel = 'Save ' + (r.title || 'product');
      var activeCls = isWishlisted(productId) ? ' bokbo-heart-active' : '';
      wishlistBtn =
        '<button class="bokbo-heart-btn' + activeCls + '" ' +
            'type="button" ' +
            'data-product-id="' + escapeHtml(productId) + '" ' +
            'data-handle="' + escapeHtml(productHandle) + '" ' +
            'data-title="' + escapeHtml(r.title || '') + '" ' +
            'aria-label="' + escapeHtml(wishlistAriaLabel) + '">' +
          '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
          '</svg>' +
        '</button>';
    }

    // BokBo Quick View — custom modal that fetches product data from
    // /products/{handle}.js and renders it without relying on Dawn's
    // custom elements (<modal-opener>, <quick-add-modal>), which don't
    // initialize properly when injected dynamically.
    var quickViewHtml = productHandle
      ? '<div class="bokbo-results-card-actions">' +
            '<button class="bokbo-quick-view-btn" type="button" ' +
              'data-bokbo-quick-view="' + escapeHtml(productHandle) + '" ' +
              'data-product-url="' + escapeHtml(r.url || '') + '">' +
              'Quick view' +
            '</button>' +
        '</div>'
      : '';

    // Card structure:
    //   .bokbo-results-card-outer
    //     [wishlist btn, absolute-positioned]
    //     .bokbo-results-card-wrap
    //       <a class="bokbo-results-card">
    //         [image]
    //         .bokbo-results-card-body
    //           .bokbo-card-brand       ← replaces the old breadcrumb
    //           .bokbo-results-card-title
    //           .bokbo-results-card-price
    //       [quick view button]
    return '<div class="bokbo-results-card-outer">' +
      wishlistBtn +
      '<div class="bokbo-results-card-wrap">' +
        '<a class="bokbo-results-card" href="' + escapeHtml(r.url || '#') + '">' +
          imgHtml +
          '<div class="bokbo-results-card-body">' +
            brandHtml +
            '<div class="bokbo-results-card-title">' + escapeHtml(r.title || '') + '</div>' +
            priceHtml +
            sizesHtml +
            sizeNoteHtml +
          '</div>' +
        '</a>' +
        quickViewHtml +
      '</div>' +
    '</div>';
  }

  /**
   * True iff the supplied size string matches the current active size
   * filter (loose case-insensitive equality on the normalized part
   * after any "group:" prefix).
   *
   * activeFilters size values arrive in three possible shapes:
   *   - clothing:M, shoe:8, kids:4T — the size dropdowns' own format
   *   - bare "M" — from NLP or direct URL param
   * We normalize both sides to the post-colon form for comparison.
   */
  function isActiveSize(sizeStr) {
    var activeVal = activeFilters.size_clothing ||
                    activeFilters.size_shoe ||
                    activeFilters.size_kids;
    if (!activeVal) return false;
    var normalized = activeVal.indexOf(':') !== -1
      ? activeVal.split(':')[1]
      : activeVal;
    return String(sizeStr || '').toLowerCase() === String(normalized || '').toLowerCase();
  }

  /**
   * Fix 2 — Return the currently active size (normalized, post-":"-prefix),
   * or '' when no size filter is set. Used by buildCardHtml to decide
   * whether to annotate a card with the "Other sizes available" note.
   */
  function getActiveSizeRaw() {
    var activeVal = activeFilters.size_clothing ||
                    activeFilters.size_shoe ||
                    activeFilters.size_kids;
    if (!activeVal) return '';
    return activeVal.indexOf(':') !== -1
      ? activeVal.split(':')[1]
      : activeVal;
  }

  /** Build the cascading filter bar HTML from the current allResults. */
  function buildFilterBarHtml() {
    var opts = buildFilterOptions();

    var html = '<div class="bokbo-filter-bar" id="bokbo-filter-bar">';

    // ── Row 1: Cascading category filters ──
    html += '<div class="bokbo-filter-row" id="bokbo-filter-row-cascade">';

    // Level 1: Gender
    html += '<select id="bokbo-filter-category" data-filter="category">';
    html += '<option value="">Gender</option>';
    for (var i = 0; i < opts.genders.length; i++) {
      html += '<option value="' + escapeHtml(opts.genders[i]) + '">' + escapeHtml(opts.genders[i]) + '</option>';
    }
    html += '</select>';

    // Level 2: Product Type (hidden until gender selected)
    if (activeFilters.category && opts.types.length > 0) {
      html += '<select id="bokbo-filter-product_type" data-filter="product_type">';
      html += '<option value="">Product Type</option>';
      for (var ti = 0; ti < opts.types.length; ti++) {
        html += '<option value="' + escapeHtml(opts.types[ti]) + '">' + escapeHtml(opts.types[ti]) + '</option>';
      }
      html += '</select>';
    }

    // Level 3: Subcategory (hidden until product type selected)
    if (activeFilters.product_type && opts.subcategories.length > 0) {
      html += '<select id="bokbo-filter-subcategory" data-filter="subcategory">';
      html += '<option value="">Subcategory</option>';
      for (var si2 = 0; si2 < opts.subcategories.length; si2++) {
        html += '<option value="' + escapeHtml(opts.subcategories[si2]) + '">' + escapeHtml(opts.subcategories[si2]) + '</option>';
      }
      html += '</select>';
    }

    html += '</div>'; // end cascade row

    // ── Row 2: Refinement filters ──
    html += '<div class="bokbo-filter-row" id="bokbo-filter-row-refine">';

    // Price Range
    html += '<select id="bokbo-filter-price" data-filter="price">';
    html += '<option value="">All Prices</option>';
    for (var j = 0; j < PRICE_RANGES.length; j++) {
      html += '<option value="' + j + '">' + escapeHtml(PRICE_RANGES[j].label) + '</option>';
    }
    html += '</select>';

    // Color
    if (opts.colors.length > 0) {
      html += '<select id="bokbo-filter-color" data-filter="color">';
      html += '<option value="">All Colors</option>';
      for (var k = 0; k < opts.colors.length; k++) {
        html += '<option value="' + escapeHtml(opts.colors[k]) + '">' + escapeHtml(opts.colors[k]) + '</option>';
      }
      html += '</select>';
    }

    // Brand
    if (opts.brands.length > 0) {
      html += '<select id="bokbo-filter-brand" data-filter="brand">';
      html += '<option value="">All Brands</option>';
      for (var bi = 0; bi < opts.brands.length; bi++) {
        html += '<option value="' + escapeHtml(opts.brands[bi]) + '">' + escapeHtml(opts.brands[bi]) + '</option>';
      }
      html += '</select>';
    }

    // Size dropdowns — contextual based on product type selection
    // Footwear → Shoe Size; Kids → Kids Size; everything else → Clothing Size
    var isFootwear = FOOTWEAR_TYPES[activeFilters.product_type] || false;
    var isKids = activeFilters.category === 'Kids';
    var sizeDropdowns = [];
    if (isKids && opts.size_kids.length > 0) {
      sizeDropdowns.push({ key: 'size_kids', label: 'Kids Size', options: opts.size_kids });
    } else if (isFootwear && opts.size_shoe.length > 0) {
      sizeDropdowns.push({ key: 'size_shoe', label: 'Shoe Size', options: opts.size_shoe });
    } else {
      // Show whichever size types are available
      if (opts.size_clothing.length > 0) {
        sizeDropdowns.push({ key: 'size_clothing', label: 'Clothing Size', options: opts.size_clothing });
      }
      if (opts.size_shoe.length > 0) {
        sizeDropdowns.push({ key: 'size_shoe', label: 'Shoe Size', options: opts.size_shoe });
      }
      if (opts.size_kids.length > 0) {
        sizeDropdowns.push({ key: 'size_kids', label: 'Kids Size', options: opts.size_kids });
      }
    }

    for (var sd = 0; sd < sizeDropdowns.length; sd++) {
      var dd = sizeDropdowns[sd];
      html += '<select id="bokbo-filter-' + dd.key + '" data-filter="' + dd.key + '">';
      html += '<option value="">All ' + escapeHtml(dd.label) + 's</option>';
      for (var m = 0; m < dd.options.length; m++) {
        html += '<option value="' + escapeHtml(dd.options[m].value) + '">' + escapeHtml(dd.options[m].label) + '</option>';
      }
      html += '</select>';
    }

    // Sort By
    html += '<select id="bokbo-sort" data-filter="sort">';
    for (var si = 0; si < SORT_OPTIONS.length; si++) {
      var selected = SORT_OPTIONS[si].value === activeSort ? ' selected' : '';
      html += '<option value="' + SORT_OPTIONS[si].value + '"' + selected + '>' + escapeHtml(SORT_OPTIONS[si].label) + '</option>';
    }
    html += '</select>';

    // Clear Filters — push to right end of refine row
    html += '<button class="bokbo-filter-clear" id="bokbo-filter-clear" disabled>Clear Filters</button>';

    html += '</div>'; // end refine row

    // Filter match count (shown when filters are active) — its own row
    html += '<span class="bokbo-filter-count" id="bokbo-filter-match-count"></span>';

    html += '</div>';
    return html;
  }

  /** Map filter keys to readable labels for the pills. */
  var FILTER_LABELS = {
    category: 'Gender',
    product_type: 'Product Type',
    subcategory: 'Subcategory',
    price: 'Price',
    color: 'Color',
    brand: 'Brand',
    size_clothing: 'Clothing Size',
    size_shoe: 'Shoe Size',
    size_kids: 'Kids Size',
  };

  /** Get the display text for an active filter's current value. */
  function getFilterDisplayValue(key) {
    var val = activeFilters[key];
    if (!val) return '';
    if (key === 'price') {
      var idx = Number(val);
      return PRICE_RANGES[idx] ? PRICE_RANGES[idx].label : val;
    }
    if (key === 'size_clothing' || key === 'size_shoe' || key === 'size_kids') {
      // 'clothing:M' → 'M'
      var parts = val.split(':');
      return parts.length === 2 ? parts[1] : val;
    }
    return val;
  }

  /** Build or update the active-filter pill strip below the filter bar. */
  function updateFilterPills() {
    var existing = document.getElementById('bokbo-active-pills');
    if (existing) existing.remove();

    if (!hasActiveFilters()) return;

    var pillsDiv = document.createElement('div');
    pillsDiv.className = 'bokbo-active-pills';
    pillsDiv.id = 'bokbo-active-pills';

    var keys = ['category', 'product_type', 'subcategory', 'price', 'color', 'brand', 'size_clothing', 'size_shoe', 'size_kids'];
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (!activeFilters[key]) continue;

      var displayVal = getFilterDisplayValue(key);
      var pill = document.createElement('span');
      pill.className = 'bokbo-pill';
      pill.innerHTML = escapeHtml(FILTER_LABELS[key]) + ': ' + escapeHtml(displayVal) +
        ' <button class="bokbo-pill-x" data-pill-filter="' + key + '" title="Remove ' + escapeHtml(FILTER_LABELS[key]) + ' filter">\u00d7</button>';
      pillsDiv.appendChild(pill);
    }

    // Insert pills right after the filter bar
    var bar = document.getElementById('bokbo-filter-bar');
    if (bar && bar.nextSibling) {
      bar.parentNode.insertBefore(pillsDiv, bar.nextSibling);
    } else if (bar) {
      bar.parentNode.appendChild(pillsDiv);
    }
  }

  /** Attach change listeners to all filter dropdowns and the clear button. */
  function wireFilterEvents() {
    var bar = document.getElementById('bokbo-filter-bar');
    if (!bar) return;

    // Delegate change events on the filter bar
    bar.addEventListener('change', function (e) {
      var sel = e.target;
      if (!sel.dataset || !sel.dataset.filter) return;

      // Sort dropdown — sort locally, no API call needed
      if (sel.dataset.filter === 'sort') {
        activeSort = sel.value;
        saveSortPreference(activeSort);
        applyFilters();
        // Re-render grid with new sort order
        shownCount = 0;
        grid.innerHTML = '';
        console.log('[BokBo] STEP 5 — about to render, top 3:',
          filteredResults.slice(0, 3).map(function (r) { return r.title; }));
        appendNextBatch();
        if (filteredResults.length > BOKBO.perPage) {
          startScrollListener();
        }
        return;
      }

      var filterKey = sel.dataset.filter;
      var prevValue = activeFilters[filterKey];
      activeFilters[filterKey] = sel.value;
      console.log('[BokBo Results] Filter changed:', filterKey, '→', sel.value === '' ? '(all)' : sel.value);

      // Remember an explicit "reset to all" intent so subsequent
      // requests tell the server not to re-detect this attribute from
      // the query text (same pattern as userClearedColor).
      if (filterKey === 'brand' && !sel.value) {
        userClearedBrand = true;
        // Drop the `bokbo_brand` URL param so a page refresh or a
        // shared link doesn't resurrect brand mode. Use replaceState
        // instead of pushState — this isn't a navigable state change.
        try {
          var u = new URL(window.location.href);
          if (u.searchParams.has('bokbo_brand')) {
            u.searchParams.delete('bokbo_brand');
            window.history.replaceState(null, '', u.toString());
          }
        } catch (_) {}
      }
      if (filterKey === 'color' && !sel.value && prevValue) {
        userClearedColor = true;
      }

      // ── Cascade logic ──
      // Changing a higher-level filter clears all lower levels and sizes.
      var needsRebuild = false;
      if (filterKey === 'category') {
        activeFilters.product_type = '';
        activeFilters.subcategory = '';
        activeFilters.size_clothing = '';
        activeFilters.size_shoe = '';
        activeFilters.size_kids = '';
        needsRebuild = true; // Level 2/3 visibility changes
      } else if (filterKey === 'product_type') {
        activeFilters.subcategory = '';
        activeFilters.size_clothing = '';
        activeFilters.size_shoe = '';
        activeFilters.size_kids = '';
        needsRebuild = true; // Level 3 + size visibility changes
      }

      // Rebuild entire filter bar if cascade levels changed
      if (needsRebuild) {
        rebuildFilterBar();
      } else {
        // Highlight active selects
        sel.classList.toggle('bokbo-filter-active', sel.value !== '');
      }

      onFiltersChanged();
    });

    // Clear button
    var clearBtn = document.getElementById('bokbo-filter-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        // "Clear Filters" wipes everything including any NLP-detected color
        // or server-detected brand — treat as explicit dismissals so the
        // server doesn't silently re-apply them on the next request.
        if (activeFilters.color) userClearedColor = true;
        if (activeFilters.brand) userClearedBrand = true;

        activeFilters = {
          category: '', product_type: '', subcategory: '',
          price: '', color: '', brand: '',
          size_clothing: '', size_shoe: '', size_kids: ''
        };

        // Strip bokbo_brand from the URL so refresh / share-link doesn't
        // re-enter brand mode either.
        try {
          var u = new URL(window.location.href);
          if (u.searchParams.has('bokbo_brand')) {
            u.searchParams.delete('bokbo_brand');
            window.history.replaceState(null, '', u.toString());
          }
        } catch (_) {}

        // Rebuild entire bar (hides cascade levels)
        rebuildFilterBar();
        onFiltersChanged();
      });
    }

    // Pill X buttons — delegate on the container so pills can be rebuilt freely
    container.addEventListener('click', function (e) {
      var xBtn = e.target.closest('.bokbo-pill-x');
      if (!xBtn) return;

      var filterKey = xBtn.getAttribute('data-pill-filter');
      if (!filterKey || !activeFilters.hasOwnProperty(filterKey)) return;

      // Clear that filter plus cascade children
      activeFilters[filterKey] = '';
      if (filterKey === 'category') {
        activeFilters.product_type = '';
        activeFilters.subcategory = '';
        activeFilters.size_clothing = '';
        activeFilters.size_shoe = '';
        activeFilters.size_kids = '';
      } else if (filterKey === 'product_type') {
        activeFilters.subcategory = '';
        activeFilters.size_clothing = '';
        activeFilters.size_shoe = '';
        activeFilters.size_kids = '';
      } else if (filterKey === 'color') {
        // Remember that the user explicitly dismissed the NLP-detected color
        // so the server doesn't silently re-apply it on the next refresh.
        // Without this, "red dress" → remove red pill → server re-detects
        // "red" from the raw query and hard-filters again.
        userClearedColor = true;
      } else if (filterKey === 'brand') {
        // Same pattern for brand: dismissing the brand pill must persist
        // as "no brand for this session", otherwise the next request's
        // NLP would detect the brand name from the query and re-enter
        // brand mode, effectively undoing the user's action.
        userClearedBrand = true;
        try {
          var u = new URL(window.location.href);
          if (u.searchParams.has('bokbo_brand')) {
            u.searchParams.delete('bokbo_brand');
            window.history.replaceState(null, '', u.toString());
          }
        } catch (_) {}
      }

      // Rebuild bar to reflect cascade changes
      rebuildFilterBar();
      onFiltersChanged();
    });
  }

  /**
   * Rebuild the entire filter bar HTML from scratch.
   * Generates new HTML from buildFilterBarHtml(), replaces the DOM element,
   * restores currently-active filter selections, and re-wires event listeners.
   * This handles cascade visibility (Level 2/3 appear/disappear based on selections).
   */
  function rebuildFilterBar() {
    var oldBar = document.getElementById('bokbo-filter-bar');
    if (!oldBar) return;

    // Generate new bar HTML
    var temp = document.createElement('div');
    temp.innerHTML = buildFilterBarHtml();
    var newBar = temp.firstChild;

    // Replace in DOM
    oldBar.parentNode.replaceChild(newBar, oldBar);

    // Restore selected values for all active filters
    var filterKeys = ['category', 'product_type', 'subcategory', 'price', 'color', 'brand',
                      'size_clothing', 'size_shoe', 'size_kids'];
    for (var i = 0; i < filterKeys.length; i++) {
      var key = filterKeys[i];
      var val = activeFilters[key];
      if (!val) continue;
      var sel = document.getElementById('bokbo-filter-' + key);
      if (sel && sel.querySelector('option[value="' + CSS.escape(val) + '"]')) {
        sel.value = val;
        sel.classList.add('bokbo-filter-active');
      } else {
        // Option no longer exists in the new result set — clear the filter
        activeFilters[key] = '';
      }
    }

    // Restore sort selection
    var sortSel = document.getElementById('bokbo-sort');
    if (sortSel) sortSel.value = activeSort;

    // Re-wire event listeners on the new bar element
    wireFilterEvents();

    // Update clear button state
    var clearBtn = document.getElementById('bokbo-filter-clear');
    if (clearBtn) clearBtn.disabled = !hasActiveFilters();

    var opts = buildFilterOptions();
    console.log('[BokBo Results] Filter bar rebuilt — genders:', opts.genders.length, 'types:', opts.types.length, 'subs:', opts.subcategories.length, 'colors:', opts.colors.length, 'brands:', opts.brands.length);
  }

  /**
   * Rebuild filter dropdowns after API results change.
   * Delegates to rebuildFilterBar() which replaces the entire bar and restores selections.
   */
  function rebuildFilterDropdowns() {
    rebuildFilterBar();
  }

  /** Called whenever a filter changes — make a new API call with filters. */
  function onFiltersChanged() {
    // Stop any in-flight scroll listener
    stopScrollListener();

    // Reset grid and show loading indicator
    shownCount = 0;
    isAppending = false;
    grid.innerHTML = '<div class="bokbo-results-loading" style="padding:40px 0;">' +
      '<div class="bokbo-results-spinner"></div>' +
      '<div class="bokbo-results-loading-text">Filtering\u2026</div>' +
      '</div>';

    // Update clear button state and pills
    var clearBtn = document.getElementById('bokbo-filter-clear');
    if (clearBtn) clearBtn.disabled = !hasActiveFilters();
    updateFilterPills();

    // Build filters and call API
    var apiFilters = buildApiFilters();
    console.log('[BokBo Results] Fetching with filters:', JSON.stringify(apiFilters || {}));

    searchApi(query, apiFilters, function (err, data) {
      if (err) {
        console.error('[BokBo Results] Filter API error:', err);
        grid.innerHTML = '<div class="bokbo-scroll-end" style="padding:40px 0;">Something went wrong. Please try again.</div>';
        return;
      }

      allResults = data && data.results ? data.results : [];
      console.log('[BokBo] STEP 1 — raw results from server, top 3:',
        allResults.slice(0, 3).map(function (r) {
          return r.title + ' sizes:' + JSON.stringify((r.available_sizes || []).slice(0, 4));
        }));
      updateServerHandledSizeFromMeta(data);
      // Diagnostic — arrival order on the filter-change path.
      try {
        console.log('[BokBo] received results order (filter-change):');
        (data && data.results ? data.results : []).slice(0, 5).forEach(function (r, i) {
          console.log('  ' + (i + 1) + '. ' + (r.title || '(no title)') +
            ' — sizes: ' + ((r.available_sizes || []).join(', ') || '(none)'));
        });
        console.log('[BokBo] applied_filters:', data && data.meta && data.meta.applied_filters);
        console.log('[BokBo] relaxed_filters:', data && data.meta && data.meta.relaxed_filters);
      } catch (_) {}
      applyFilters();

      // Rebuild dropdowns from new results (preserves selected values)
      rebuildFilterDropdowns();
      updateFilterPills();

      // Update match count
      var matchLabel = document.getElementById('bokbo-filter-match-count');
      if (matchLabel) {
        matchLabel.textContent = hasActiveFilters()
          ? filteredResults.length + ' match' + (filteredResults.length !== 1 ? 'es' : '')
          : '';
      }

      // Clear grid (remove spinner)
      grid.innerHTML = '';

      if (filteredResults.length === 0) {
        // Server can emit meta.no_results_reason when size is the
        // sole culprit — render a dedicated empty state rather than
        // the generic "no products match" string. Helper decides
        // whether to write into the grid or the container and wires
        // its own Clear-size button.
        if (!handleNoResultsReason(data)) {
          grid.innerHTML = '<div class="bokbo-scroll-end" style="padding:40px 0;">No products match the selected filters.</div>';
        }
        updateCountLabel();
        return;
      }

      // Append the first batch and restart scroll listener
      console.log('[BokBo] STEP 5 — about to render, top 3:',
        filteredResults.slice(0, 3).map(function (r) { return r.title; }));
      appendNextBatch();
      renderSizeRelaxationNotice(data);
      saveBackButtonCache();
      if (filteredResults.length > BOKBO.perPage) {
        startScrollListener();
      }

      // Scroll back to top of results
      var header = container.querySelector('.bokbo-results-header');
      if (header) header.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /** Build the initial page shell: header + filter bar + empty grid + footer. */
  function renderShell() {
    // Always use the raw query (from URL ?q= param) for the page title,
    // not the NLP-stripped version the API uses internally.
    // e.g. "summer dress" stays "summer dress", not just "dress".
    document.title = 'Search results for \u201C' + query + '\u201D';

    container.innerHTML =
      '<div class="bokbo-results-header">' +
        '<h1>Search results for \u201C' + escapeHtml(query) + '\u201D</h1>' +
        '<p id="bokbo-results-count"></p>' +
        '<div class="bokbo-results-search-box">' +
          '<input type="search" id="bokbo-results-search-input" placeholder="Refine your search\u2026" value="' + escapeHtml(query) + '" autocomplete="off" />' +
        '</div>' +
      '</div>' +
      buildFilterBarHtml() +
      '<div class="bokbo-results-grid" id="bokbo-grid"></div>' +
      '<div class="bokbo-results-footer">' +
        '<a href="https://bokbo.com" target="_blank" rel="noopener">Powered by BokBo</a>' +
      '</div>';

    grid = document.getElementById('bokbo-grid');
    ensureQuickViewListener();
    wireFilterEvents();
    wireResultsSearchBox();

    // Share-my-picks button — only emit when the wishlist feature is
    // turned on for this store. DOM-create (not innerHTML) because the
    // button is a live click target; prepending it to the header keeps
    // it above the search-refine input, near the result count.
    if (isWishlistEnabled()) {
      var headerEl = container.querySelector('.bokbo-results-header');
      if (headerEl) headerEl.appendChild(renderShareButton());
    }
  }

  function updateCountLabel() {
    var label = document.getElementById('bokbo-results-count');
    if (!label) return;
    var total = filteredResults.length;
    var suffix = hasActiveFilters() ? ' (filtered from ' + allResults.length + ')' : '';
    label.textContent = total + ' result' +
      (total !== 1 ? 's' : '') +
      ' found \u2014 showing ' + shownCount + ' of ' + total + suffix;
  }

  /** Append the next batch of cards into the grid. */
  // Back-button cache write helper. Called from the initial-load and
  // re-search callbacks once filteredResults has been populated. The
  // cap at 48 keeps the sessionStorage payload small — enough for
  // two scroll batches, which is what fits above the fold on most
  // devices. Wrapped in try/catch so a quota-exceeded error (iOS
  // Safari caps sessionStorage at ~5MB) can't break rendering.
  // Back-button cache key. Bump the suffix whenever the ranking pipeline
  // changes materially (size-first sort, new rerank, combined-filter
  // relaxation, etc.) so pre-fix cached result sets from older widget
  // builds can't paint over a correctly-ordered fresh response.
  //
  // Version history:
  //   v1 — original back-button cache (bokbo_last_results)
  //   v2 — added after the first size-sort fix
  //   v3 — bumped alongside the "cache paint leaves shownCount stale"
  //        render-re-run fix in init(); v2 entries carry the wrong
  //        sort order from before applySizeSort was wired through
  //        originalSize, so we force-expire them on upgrade.
  // Old keys are explicitly removed below so stale data doesn't linger
  // through the rest of the tab session.
  var BACK_CACHE_KEY = 'bokbo_last_results_v3';

  // DISABLED — the back-button cache is serving stale pre-fix results
  // (wrong size sort order) and the background-refresh re-render path
  // isn't reliably overwriting the cache paint. Keeping the function
  // as a no-op so every caller (init, runResearch, brand follow-up)
  // keeps compiling, but nothing gets written to sessionStorage.
  //
  // To re-enable: restore the original implementation (build payload
  // → setItem(BACK_CACHE_KEY, JSON.stringify(payload)) inside a
  // try/catch) AND verify that the background-refresh path in init()
  // properly resets shownCount + clears the grid before re-rendering.
  // Past incidents logged under bokbo_last_results v1/v2/v3 bumps.
  function saveBackButtonCache() {
    /* no-op — see comment above */
  }

  function appendNextBatch() {
    console.log('[BokBo] STEP 6 — rendering cards, shownCount:', shownCount,
      'filteredResults length:', filteredResults.length);
    if (isAppending || shownCount >= filteredResults.length) return;
    isAppending = true;

    var batch = filteredResults.slice(shownCount, shownCount + BOKBO.perPage);
    if (batch.length === 0) { isAppending = false; return; }

    // First batch (shownCount === 0) paints immediately — no loader dots,
    // no artificial delay. Subsequent batches (infinite scroll) show the
    // loader so the shopper feels the "fetching more" transition even
    // though everything is already client-side.
    var isFirstBatch = shownCount === 0;

    if (!isFirstBatch) {
      var loader = document.createElement('div');
      loader.className = 'bokbo-scroll-loader';
      loader.id = 'bokbo-scroll-loader';
      loader.innerHTML =
        '<div class="bokbo-scroll-loader-dot"></div>' +
        '<div class="bokbo-scroll-loader-dot"></div>' +
        '<div class="bokbo-scroll-loader-dot"></div>';
      grid.appendChild(loader);
    }

    // paint() batches DOM work: builds the whole batch as a single HTML
    // string, parses it once into a wrap, then moves children into a
    // DocumentFragment. One parse + one layout cost instead of N of each.
    var paint = function () {
      // Remove loader (if it was rendered).
      var ldr = document.getElementById('bokbo-scroll-loader');
      if (ldr) ldr.remove();

      var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
      var html = '';
      for (var i = 0; i < batch.length; i++) {
        html += buildCardHtml(batch[i]);
      }
      var wrap = document.createElement('div');
      wrap.innerHTML = html;
      var fragment = document.createDocumentFragment();
      while (wrap.firstChild) fragment.appendChild(wrap.firstChild);
      grid.appendChild(fragment);

      // Quick View click delegation is handled once at the grid level
      // (see ensureQuickViewListener below). No per-batch DOM injection needed.

      // Reconcile heart-button fill state against localStorage. Cards
      // are rendered with the active class pre-computed in
      // buildCardHtml, but paint state can drift if the shopper has
      // multiple BokBo tabs open (another tab toggling a heart writes
      // to localStorage but doesn't paint in this tab).
      updateHeartButtons();

      shownCount += batch.length;
      updateCountLabel();
      var t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
      console.log('[BokBo Results] Appended', batch.length, 'cards in', Math.round(t1 - t0), 'ms — now showing', shownCount, 'of', filteredResults.length);

      // If all results are now shown, show end message and remove scroll listener
      if (shownCount >= filteredResults.length) {
        if (filteredResults.length > BOKBO.perPage) {
          var endMsg = document.createElement('div');
          endMsg.className = 'bokbo-scroll-end';
          endMsg.textContent = 'You\u2019ve seen all ' + filteredResults.length + ' results';
          grid.appendChild(endMsg);
        }
        stopScrollListener();
      }

      isAppending = false;
    };

    if (isFirstBatch) {
      paint(); // synchronous — zero wasted ms
    } else {
      // Small delay so the loader is visible (feels intentional, not janky)
      setTimeout(paint, 150);
    }
  }

  /** Scroll handler — fires next batch when within 500px of page bottom. */
  function onWindowScroll() {
    if (isAppending || shownCount >= filteredResults.length) return;
    if (window.scrollY + window.innerHeight >= document.body.scrollHeight - 500) {
      console.log('[BokBo] STEP 5 — about to render, top 3:',
        filteredResults.slice(0, 3).map(function (r) { return r.title; }));
      appendNextBatch();
    }
  }

  /** Attach the window scroll listener. */
  function startScrollListener() {
    if (!BOKBO.infiniteScroll) {
      // Infinite scroll disabled — eagerly append all remaining batches
      // so the full result set is visible without scrolling.
      console.log('[BokBo Results] Infinite scroll disabled — rendering all results up-front');
      var drainAll = function () {
        if (shownCount >= filteredResults.length) return;
        console.log('[BokBo] STEP 5 — about to render, top 3:',
          filteredResults.slice(0, 3).map(function (r) { return r.title; }));
        appendNextBatch();
        // appendNextBatch uses a small 150ms timeout internally
        setTimeout(drainAll, 160);
      };
      drainAll();
      return;
    }
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    console.log('[BokBo Results] Scroll listener attached');
  }

  /** Remove the window scroll listener (called when all results are shown). */
  function stopScrollListener() {
    window.removeEventListener('scroll', onWindowScroll);
    console.log('[BokBo Results] Scroll listener removed — all results shown');
  }

  // ── API call ────────────────────────────────────────────────────

  /**
   * Build the filters object to send to the API from activeFilters.
   * Translates client-side filter state into the shape the server expects.
   */
  function buildApiFilters() {
    var f = {};

    if (activeFilters.category) {
      f.category = activeFilters.category;
    }

    if (activeFilters.product_type) {
      f.product_type = activeFilters.product_type;
    }

    if (activeFilters.subcategory) {
      f.subcategory = activeFilters.subcategory;
    }

    if (activeFilters.price !== '') {
      var idx = Number(activeFilters.price);
      var range = PRICE_RANGES[idx];
      if (range) {
        if (range.min > 0) f.price_min = range.min;
        if (range.max !== Infinity) f.price_max = range.max;
      }
    }

    if (activeFilters.color) {
      f.color = activeFilters.color;
    }

    // Size dropdown — forward the group-prefixed value verbatim so the
    // server can route correctly.
    //
    // size_shoe stores values like "shoe:11"; size_clothing stores
    // "clothing:M" / "numeric:12" / "plus:2X"; size_kids stores
    // "kids:7". The server uses the `shoe:` prefix to decide whether
    // bare "11" should fan through the SHOE alias table (11, US11,
    // 11M, Men 11, EU45, UK11) or the dress-size ladder (which maps
    // 12 → L class → [L, Large, 10, 12] and poisons a "shoes 12"
    // query with clothing-Large and shoe-10 products).
    //
    // Previous behavior stripped the prefix here and sent bare "11",
    // which caused server-side NLP to see filters.size already set
    // and skip its own shoe-prefix enrichment — the dress-size alias
    // leak was the visible result. Keep the prefix on the wire; the
    // server strips it where appropriate inside buildQdrantFilters
    // and applySizeSort.
    //
    // Priority order: shoe > kids > clothing. If a shopper has both
    // a shoe and a clothing size selected (rare, possible if they
    // kept prior filters across categories), the shoe dropdown wins
    // for the API request since it carries the more specific hint.
    if (activeFilters.size_shoe) {
      f.size = activeFilters.size_shoe;
    } else if (activeFilters.size_kids) {
      f.size = activeFilters.size_kids;
    } else if (activeFilters.size_clothing) {
      f.size = activeFilters.size_clothing;
    }

    if (activeFilters.brand) {
      f.brand = activeFilters.brand;
    }

    return Object.keys(f).length > 0 ? f : undefined;
  }

  function searchApi(q, filters, callback) {
    var xhr = new XMLHttpRequest();
    xhr.open('POST', BOKBO.apiUrl, true);
    xhr.setRequestHeader('Content-Type', 'application/json');

    // Network-level error surfacing. `onerror` fires on DNS / CORS /
    // network failures where `onreadystatechange` never reaches
    // readyState 4. Previously these errored silently and the widget
    // just sat in its loading state — that's the most likely cause of
    // "[BokBo] searching with: fires, [BokBo] received results order: never".
    xhr.onerror = function () {
      console.error('[BokBo] fetch error: XHR onerror fired — network / CORS failure for', BOKBO.apiUrl);
      try { callback(new Error('XHR network error'), null); }
      catch (cbErr) { console.error('[BokBo] callback threw on network error:', cbErr); }
    };
    xhr.ontimeout = function () {
      console.error('[BokBo] fetch error: XHR timed out after', xhr.timeout, 'ms for', BOKBO.apiUrl);
      try { callback(new Error('XHR timeout'), null); }
      catch (cbErr) { console.error('[BokBo] callback threw on timeout:', cbErr); }
    };
    xhr.onabort = function () {
      console.warn('[BokBo] XHR aborted for', BOKBO.apiUrl);
    };

    xhr.onreadystatechange = function () {
      // Only log the terminal state (readyState 4 = DONE). Intermediate
      // states (1/2/3) fired four lines per request while we were
      // chasing a silent CORS issue; with that fixed they're noise.
      // onerror / ontimeout / onabort above still catch the "never
      // reached state 4" failure modes with their own loud errors.
      if (xhr.readyState !== 4) return;
      console.log('[BokBo] XHR done: status=' + xhr.status + ', bytes=' + (xhr.responseText ? xhr.responseText.length : 0));

      if (xhr.status >= 200 && xhr.status < 300) {
        var data = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (parseErr) {
          console.error('[BokBo] fetch error: JSON parse failed —', parseErr && parseErr.message);
          try { callback(parseErr, null); }
          catch (cbErr) { console.error('[BokBo] callback threw on parse error:', cbErr); }
          return;
        }
        // Raw response arrived — log the first title so a "response
        // arrived but size sort looks wrong" case separates cleanly
        // from a "response never arrived" case.
        try {
          var firstTitle = data && data.results && data.results[0] && data.results[0].title;
          console.log('[BokBo] raw response arrived, first title:', firstTitle || '(empty or missing)');
        } catch (_) {}
        try {
          callback(null, data);
        } catch (cbErr) {
          console.error('[BokBo] fetch error: callback threw —', cbErr && cbErr.stack || cbErr);
        }
      } else {
        console.error('[BokBo] fetch error: non-2xx status =', xhr.status, 'body:', (xhr.responseText || '').slice(0, 200));
        try { callback(new Error('Search API ' + xhr.status), null); }
        catch (cbErr) { console.error('[BokBo] callback threw on non-2xx:', cbErr); }
      }
    };

    var body = {
      query: q,
      store_id: BOKBO.storeId,
      limit: BOKBO.resultLimit,
    };
    if (filters) body.filters = filters;
    // Signal to the server that this is a direct brand search (user clicked a brand name).
    // Server should skip all NLP brand detection and vendor matching.
    // Only send brandDirect when the brand filter is still active — if the user
    // cleared the brand pill, we must NOT re-send brandDirect or the server
    // will re-detect the brand name from the query via NLP.
    var urlP = new URLSearchParams(window.location.search);
    if (urlP.get('bokbo_brand') === '1' && activeFilters.brand) body.brandDirect = true;
    // Suppress server-side brand detection in TWO scenarios:
    //   (a) URL indicates this came from a brand click but the filter is
    //       now cleared (the user backed out of the implicit brand scope).
    //   (b) The user explicitly reset the brand dropdown / pill — the
    //       URL might not have bokbo_brand (e.g. a semantic search where
    //       the server auto-detected the brand), but we still need the
    //       server to stop re-detecting that brand from the query text.
    // Either condition sets noBrandDetect. Fixes the "brand reset to All
    // does nothing" bug where the server kept re-applying brand mode.
    if (
      (urlP.get('bokbo_brand') === '1' && !activeFilters.brand) ||
      (userClearedBrand && !activeFilters.brand)
    ) {
      body.noBrandDetect = true;
    }
    // If the user explicitly cleared the NLP-detected color pill, tell the
    // server to skip color extraction so it doesn't re-apply on refresh.
    if (userClearedColor && !activeFilters.color) body.noColorDetect = true;

    // Step-4 diagnostic — log the EXACT payload about to go on the
    // wire. Useful when the browser shows different results than a
    // curl against /search: comparing the logged body to the curl
    // body is the fastest way to spot a widget-side mutation (e.g.
    // size getting dropped from filters, or the raw query being
    // pre-cleaned before it reaches the server's NLP). Log the
    // stringified form so nested objects are fully visible.
    try { console.log('[BokBo] searching with:', JSON.stringify(body)); } catch (_) {}

    // Step-3 diagnostic — log the exact URL this XHR is posting to.
    // BOKBO.apiUrl is now resolved at init time from
    // window.BOKBO_CONFIG.apiUrl if the theme sets that, else the
    // vanity default. Logging every request makes CORS failures
    // attributable on sight: if "posting to: https://bokbo.com/search"
    // but the theme config says Railway, that's a config drift. If
    // the URL looks right but XHR state changes stall at 1, it's a
    // network / CORS preflight rejection.
    try { console.log('[BokBo] posting to:', BOKBO.apiUrl); } catch (_) {}

    xhr.send(JSON.stringify(body));
  }

  // ── Results-page re-search box (debounced as-you-type) ──────────

  var resultsSearchTimer = null;

  /**
   * Run a full re-search against a new query text without leaving the
   * page. Used by:
   *   1. The in-page refine input (wireResultsSearchBox below) — 400ms
   *      debounced as the shopper types.
   *   2. The bokbo:navigate custom event (startSearch) — fired by
   *      bokbo-search-v3 when the shopper submits a new search from
   *      the header input on the SAME page, so we can pushState the
   *      new URL and re-render inline instead of doing a hard
   *      /pages/bokbo-search navigation with the 404 flash.
   *   3. The popstate listener — fired when the shopper hits back/
   *      forward between pushState'd BokBo searches.
   *
   * The `inputEl` param is optional — when called from the in-page
   * input it lets us toggle the `.bokbo-searching` CSS pulse on the
   * input; other callers pass null and skip the pulse.
   */
  function runResearch(val, inputEl) {
    // Update the module-level query
    query = val;

    // Update the URL without reload so the user can share / bookmark.
    // replaceState (not pushState) — the per-keystroke re-search
    // shouldn't pile history entries; only the pushState-driven
    // navigate-from-elsewhere flow creates a new history entry.
    try {
      var url = new URL(window.location);
      url.searchParams.set('q', val);
      window.history.replaceState(
        window.history.state || { bokboQuery: val },
        '',
        url.toString()
      );
    } catch (_) {}

    // Update heading
    var h1 = container && container.querySelector('.bokbo-results-header h1');
    if (h1) h1.textContent = 'Search results for \u201C' + val + '\u201D';
    document.title = 'Search results for \u201C' + val + '\u201D';

    // Reset state for the new query. A fresh query text means the
    // shopper's previous "I cleared this attribute" intents don't
    // apply — let the server re-run NLP from scratch on the new
    // string.
    userClearedColor = false;
    userClearedBrand = false;
    activeFilters = {};
    allResults = [];
    filteredResults = [];
    shownCount = 0;
    stopScrollListener();

    // Show loading
    if (grid) grid.innerHTML = '';
    renderLoading();

    var filters = buildApiFilters();
    searchApi(val, filters && Object.keys(filters).length ? filters : null, function (err, data) {
      if (inputEl) inputEl.classList.remove('bokbo-searching');

      if (err) {
        console.error('[BokBo Results] Re-search error:', err);
        renderError();
        return;
      }

      allResults = data && data.results ? data.results : [];
      console.log('[BokBo] STEP 1 — raw results from server, top 3:',
        allResults.slice(0, 3).map(function (r) {
          return r.title + ' sizes:' + JSON.stringify((r.available_sizes || []).slice(0, 4));
        }));
      updateServerHandledSizeFromMeta(data);
      console.log('[BokBo Results] Re-search received', allResults.length, 'results for:', val);

      // Diagnostic — same shape as the init() callback's log so every
      // search path (init / re-search / filter-change / brand follow-up)
      // surfaces the arrival order. This was missing and is the reason
      // the "[BokBo] received results order" log never showed in the
      // browser for shoppers who submitted from the header (which goes
      // through bokbo:navigate → runResearch, NOT init).
      try {
        console.log('[BokBo] received results order (re-search):');
        (data && data.results ? data.results : []).slice(0, 5).forEach(function (r, i) {
          console.log('  ' + (i + 1) + '. ' + (r.title || '(no title)') +
            ' — sizes: ' + ((r.available_sizes || []).join(', ') || '(none)'));
        });
        console.log('[BokBo] applied_filters:', data && data.meta && data.meta.applied_filters);
        console.log('[BokBo] relaxed_filters:', data && data.meta && data.meta.relaxed_filters);
      } catch (_) {}

      if (allResults.length === 0) {
        if (!handleNoResultsReason(data)) renderNoResults();
        return;
      }

      applyFilters();
      renderShell();

      // Re-focus the input and restore cursor position after renderShell rebuilds the DOM
      var newInput = document.getElementById('bokbo-results-search-input');
      if (newInput) {
        newInput.value = val;
        // Only refocus when the user was interacting with the
        // refine-input — jumping focus during a programmatic
        // navigate (bokbo:navigate / popstate) is disruptive.
        if (inputEl) {
          newInput.focus();
          newInput.setSelectionRange(val.length, val.length);
        }
      }

      // Color detection from new query
      if (data.detected_color && !activeFilters.color) {
        activeFilters.color = data.detected_color;
        var colorSel = document.getElementById('bokbo-filter-color');
        if (colorSel) {
          if (!colorSel.querySelector('option[value="' + CSS.escape(data.detected_color) + '"]')) {
            colorSel.innerHTML += '<option value="' + escapeHtml(data.detected_color) + '">' + escapeHtml(data.detected_color) + '</option>';
          }
          colorSel.value = data.detected_color;
          colorSel.classList.add('bokbo-filter-active');
        }
        updateFilterPills();
      }

      // Size detection from new query (parse meta.applied_filters).
      syncSizeFilterFromMeta(data);
      syncCategoryFilterFromMeta(data);

      // Brand detection
      if (data.detected_brand) {
        activeFilters.brand = data.detected_brand;
        var brandSel = document.getElementById('bokbo-filter-brand');
        if (brandSel) {
          if (!brandSel.querySelector('option[value="' + CSS.escape(data.detected_brand) + '"]')) {
            brandSel.innerHTML += '<option value="' + escapeHtml(data.detected_brand) + '">' + escapeHtml(data.detected_brand) + '</option>';
          }
          brandSel.value = data.detected_brand;
          brandSel.classList.add('bokbo-filter-active');
        }
        updateFilterPills();
      }

      console.log('[BokBo] STEP 5 — about to render, top 3:',
        filteredResults.slice(0, 3).map(function (r) { return r.title; }));
      appendNextBatch();
      renderSizeRelaxationNotice(data);
      saveBackButtonCache();
      if (filteredResults.length > BOKBO.perPage) startScrollListener();
    });
  }

  function wireResultsSearchBox() {
    var input = document.getElementById('bokbo-results-search-input');
    if (!input) return;

    input.addEventListener('input', function () {
      clearTimeout(resultsSearchTimer);
      var val = input.value.trim();
      if (val.length < 3) return;

      input.classList.add('bokbo-searching');

      resultsSearchTimer = setTimeout(function () {
        runResearch(val, input);
      }, 400); // slightly longer debounce than dropdown (400ms vs 300ms) for full re-search
    });

    // Enter key — immediate search (cancel any pending debounce)
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        clearTimeout(resultsSearchTimer);
        // Trigger input event handler immediately via a 0ms debounce
        input.dispatchEvent(new Event('input'));
      }
    });
  }

  // ── BokBo Quick View Modal ──────────────────────────────────────
  //
  // Instead of relying on Dawn's <modal-opener> / <quick-add-modal> custom
  // elements (which don't initialize for dynamically injected HTML), we
  // fetch product JSON from /products/{handle}.js and render our own modal.

  // ── Quick view modal — per-open lifecycle ──────────────────────
  //
  // Earlier version kept a singleton overlay in the DOM and toggled a
  // `bokbo-qv-visible` class on/off to open/close. That left the
  // overlay element (and whatever stale listeners were on it) attached
  // to the page forever, which in at least one close path — the inline
  // onclick on the initial loading-state close button — also failed to
  // restore `document.body.style.overflow`. The net effect: the page
  // looked normal but intercepted all clicks because the full-viewport
  // overlay was still present with pointer events enabled.
  //
  // New design:
  //   • each Open creates a fresh overlay + dialog and appends them to
  //     <body>. Nothing survives between opens.
  //   • every listener wired at open time — overlay click, close-btn
  //     click, document-level Escape, variant change, ATC click — is
  //     attached with { signal: controller.signal }, so Close aborts
  //     the controller and the browser removes them atomically.
  //   • Close removes overlay + dialog from the DOM entirely, restores
  //     body.overflow, and clears the module reference.
  //   • Re-opening while already open closes the previous instance
  //     first so no two overlays ever coexist.
  //   • Async XHR callbacks guard on `qvState.controller === controller`
  //     so a late response for a closed modal becomes a no-op.
  // qvState shape: {
  //   overlay, dialog, controller,
  //   results: array of product objects currently navigable,
  //   index:   0-based position within `results`,
  //   generation: incremented on every loadQuickViewProduct() call so
  //     async fetch callbacks can detect "a newer navigation has
  //     already started, discard my result",
  //   prevBtn, nextBtn, hintEl: references to nav UI elements so we
  //     can toggle their disabled/visibility state without requerying.
  // }
  var qvState = null;

  function bokboQuickViewOpen(handle, productUrl, resultsArray, index) {
    // Close any stale instance before opening a new one — keeps the
    // "only one modal at a time" invariant that the close logic relies on.
    if (qvState) bokboQuickViewClose();

    var controller = new AbortController();
    var signal = controller.signal;

    var overlay = document.createElement('div');
    overlay.className = 'bokbo-qv-overlay bokbo-qv-visible';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Quick view');

    var dialog = document.createElement('div');
    dialog.className = 'bokbo-qv-dialog';
    dialog.innerHTML =
      '<button class="bokbo-qv-close" aria-label="Close">\u00d7</button>' +
      '<div class="bokbo-qv-loading">Loading product\u2026</div>';

    // Desktop navigation chevrons. Live on the overlay (not inside the
    // dialog) so their absolute positioning is relative to the full
    // viewport and they stay anchored to the screen edges regardless of
    // dialog height changes between products.
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'bokbo-qv-nav bokbo-qv-nav-prev';
    prevBtn.setAttribute('aria-label', 'Previous product');
    prevBtn.innerHTML = '\u2039'; // ‹

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'bokbo-qv-nav bokbo-qv-nav-next';
    nextBtn.setAttribute('aria-label', 'Next product');
    nextBtn.innerHTML = '\u203a'; // ›

    // Mobile swipe hint — auto-fades after 2s via the fade class +
    // setTimeout below. CSS hides it on desktop, hides chevrons on
    // mobile, so each viewport gets the appropriate affordance.
    var hintEl = document.createElement('div');
    hintEl.className = 'bokbo-qv-swipe-hint';
    hintEl.textContent = '\u2190 swipe \u2192  \u00b7  swipe \u2191 to save  \u00b7  swipe \u2193 to close';

    overlay.appendChild(prevBtn);
    overlay.appendChild(dialog);
    overlay.appendChild(nextBtn);
    overlay.appendChild(hintEl);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    // Publish state BEFORE wiring listeners so any re-entry from a
    // listener sees a coherent view. resultsArray / index define the
    // navigable slice — if the caller didn't supply them we fall back
    // to a single-product state (nav buttons disable, swipe/arrows
    // no-op, but close/esc/etc. all still work).
    qvState = {
      overlay: overlay,
      dialog: dialog,
      controller: controller,
      results: Array.isArray(resultsArray) ? resultsArray : [],
      index: typeof index === 'number' && index >= 0 ? index : 0,
      generation: 0,
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      hintEl: hintEl,
    };

    // ── Close wiring (all via { signal }) ──────────────────────────
    overlay.addEventListener('click', function (e) {
      // Only a click on the overlay BACKDROP — not on nav buttons,
      // dialog, or hint — counts as a "click outside to close".
      if (e.target === overlay) bokboQuickViewClose();
    }, { signal: signal });

    var loadingCloseBtn = dialog.querySelector('.bokbo-qv-close');
    if (loadingCloseBtn) {
      loadingCloseBtn.addEventListener('click', bokboQuickViewClose, { signal: signal });
    }

    // ── Keyboard wiring (Escape + arrows) ──────────────────────────
    // Left / Right arrows navigate between results. We skip when the
    // focus is inside a form control so native arrow-key behavior
    // (cycling <select> options, moving caret in inputs) still works.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        bokboQuickViewClose();
        return;
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var ae = document.activeElement;
      if (ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') bokboQuickViewPrev();
      else bokboQuickViewNext();
    }, { signal: signal });

    // ── Nav button wiring ──────────────────────────────────────────
    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      bokboQuickViewPrev();
    }, { signal: signal });
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      bokboQuickViewNext();
    }, { signal: signal });

    // ── Swipe wiring (touch devices) ───────────────────────────────
    // Four-direction gesture map:
    //   swipe left  → next product
    //   swipe right → previous product
    //   swipe up    → save to favorites (toggles wishlist, pulses the
    //                 heart on the underlying card, toast)
    //   swipe down  → close
    //
    // The dominant axis is whichever |delta| is larger. A 60px minimum
    // on max(|dx|, |dy|) rejects small finger twitches and keeps page
    // scrolling from accidentally being interpreted as a swipe-close.
    var touchStartX = 0;
    var touchStartY = 0;
    dialog.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches.length) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { signal: signal, passive: true });
    dialog.addEventListener('touchend', function (e) {
      if (!e.changedTouches || !e.changedTouches.length) return;
      var endX = e.changedTouches[0].clientX;
      var endY = e.changedTouches[0].clientY;
      var dx = touchStartX - endX;
      var dy = touchStartY - endY;
      var absDx = Math.abs(dx);
      var absDy = Math.abs(dy);
      if (Math.max(absDx, absDy) < 60) return;

      if (absDx > absDy) {
        // Horizontal — page through results.
        if (dx > 0) bokboQuickViewNext();
        else        bokboQuickViewPrev();
      } else if (dy > 0) {
        // Swipe up — save the CURRENT product to favorites. Pull from
        // qvState.results/index so the save targets whatever the user
        // was actually looking at, even if they navigated first. Then
        // pulse the heart on the card behind the modal so there's
        // visual continuity between the gesture and the card state
        // they'll see when they close.
        if (!qvState || !qvState.results) return;
        var product = qvState.results[qvState.index];
        if (!product || !product.shopify_id) return;
        var wasAdded = toggleWishlist(product);
        showWishlistToast(wasAdded ? '\u2665 Saved to favorites' : 'Removed from favorites');
        var heartBtn = document.querySelector(
          '.bokbo-heart-btn[data-product-id="' + CSS.escape(product.shopify_id) + '"]'
        );
        if (heartBtn) {
          heartBtn.classList.add('bokbo-heart-pulse');
          setTimeout(function () { heartBtn.classList.remove('bokbo-heart-pulse'); }, 300);
        }
        updateHeartButtons();
      } else {
        // Swipe down — dismiss.
        bokboQuickViewClose();
      }
    }, { signal: signal, passive: true });

    // ── Fade the mobile swipe hint after 2s ────────────────────────
    // Tied to the signal via an AbortSignal-aware setTimeout: if the
    // modal closes before the 2s elapses, abort clears it so we don't
    // run the fade on a detached node. The setTimeout result itself is
    // harmless to leak, but this keeps intent explicit.
    var hintTimer = setTimeout(function () {
      if (hintEl && hintEl.parentNode) hintEl.classList.add('bokbo-qv-fade');
    }, 2000);
    signal.addEventListener('abort', function () { clearTimeout(hintTimer); });

    // Initial nav state (disables prev if we opened at index 0, next
    // if we opened at the end of the list).
    updateQuickViewNavState();

    // Fire the first load. This also handles all subsequent Next /
    // Prev navigations — open is just "load index N".
    loadQuickViewProduct(handle, productUrl);
  }

  /**
   * Load the given product into the currently-open modal WITHOUT
   * tearing down the overlay / dialog / listeners. Called both on
   * initial open and on every subsequent Next / Prev navigation.
   *
   * Uses qvState.generation for race protection: each call bumps the
   * counter, and fetch callbacks compare their captured generation to
   * the current one — if the user has already navigated past this
   * load, the response is silently dropped.
   */
  function loadQuickViewProduct(handle, productUrl) {
    if (!qvState) return;
    qvState.generation++;
    var gen = qvState.generation;
    var controller = qvState.controller;
    var dialog = qvState.dialog;

    // Paint the loading state immediately so the transition between
    // products feels responsive even if /products/{handle}.js is slow.
    dialog.innerHTML =
      '<button class="bokbo-qv-close" aria-label="Close">\u00d7</button>' +
      '<div class="bokbo-qv-loading">Loading product\u2026</div>';
    var loadingClose = dialog.querySelector('.bokbo-qv-close');
    if (loadingClose) {
      loadingClose.addEventListener('click', bokboQuickViewClose, { signal: controller.signal });
    }

    var xhr = new XMLHttpRequest();
    controller.signal.addEventListener('abort', function () {
      try { xhr.abort(); } catch (_) {}
    });
    xhr.open('GET', '/products/' + encodeURIComponent(handle) + '.js', true);
    xhr.onload = function () {
      // Bail if: modal closed, a newer navigation already started,
      // or the controller has been rotated (shouldn't happen, but
      // belt and suspenders).
      if (!qvState || qvState.controller !== controller || qvState.generation !== gen) return;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          var product = JSON.parse(xhr.responseText);
          renderQuickViewContent(product, productUrl || ('/products/' + handle));
        } catch (e) {
          renderQuickViewError('Could not parse product data.');
        }
      } else {
        renderQuickViewError('Product not found (HTTP ' + xhr.status + ').');
      }
    };
    xhr.onerror = function () {
      if (!qvState || qvState.controller !== controller || qvState.generation !== gen) return;
      renderQuickViewError('Network error — please try again.');
    };
    xhr.send();
  }

  /** Advance the open modal to the next product in qvState.results. */
  function bokboQuickViewNext() {
    if (!qvState || !qvState.results || !qvState.results.length) return;
    var next = qvState.index + 1;
    if (next >= qvState.results.length) return; // clamped at end
    qvState.index = next;
    var p = qvState.results[next] || {};
    updateQuickViewNavState();
    loadQuickViewProduct(p.handle, p.url);
  }

  /** Step the open modal back to the previous product in qvState.results. */
  function bokboQuickViewPrev() {
    if (!qvState || !qvState.results || !qvState.results.length) return;
    var prev = qvState.index - 1;
    if (prev < 0) return; // clamped at start
    qvState.index = prev;
    var p = qvState.results[prev] || {};
    updateQuickViewNavState();
    loadQuickViewProduct(p.handle, p.url);
  }

  /**
   * Toggle the disabled class on the nav chevrons based on the current
   * index. Called after open and after each Next/Prev. When the
   * results array is empty or a singleton, BOTH chevrons are disabled
   * (mobile swipe / arrow keys also no-op per the early returns in
   * Next / Prev), so the shopper can still close but can't try to
   * page through a non-list.
   */
  function updateQuickViewNavState() {
    if (!qvState) return;
    var total = (qvState.results || []).length;
    var atStart = qvState.index <= 0;
    var atEnd = qvState.index >= total - 1;
    if (qvState.prevBtn) {
      qvState.prevBtn.classList.toggle('bokbo-qv-nav-disabled', atStart || total < 2);
    }
    if (qvState.nextBtn) {
      qvState.nextBtn.classList.toggle('bokbo-qv-nav-disabled', atEnd || total < 2);
    }
  }

  function renderQuickViewContent(product, productUrl) {
    var img = (product.featured_image)
      ? product.featured_image
      : (product.images && product.images.length ? product.images[0] : '');

    // Format price — Shopify .js returns price in cents
    function formatCents(cents) {
      if (cents == null) return '';
      return '$' + (cents / 100).toFixed(2);
    }

    var price = product.price ? formatCents(product.price) : '';
    var compareAt = product.compare_at_price && product.compare_at_price > product.price
      ? formatCents(product.compare_at_price) : '';

    // Strip HTML tags from description, truncate
    var desc = (product.description || '').replace(/<[^>]+>/g, '').trim();
    if (desc.length > 300) desc = desc.substring(0, 300) + '\u2026';

    // Variant selector (only if > 1 variant that isn't "Default Title")
    var variants = product.variants || [];
    var showVariants = variants.length > 1 ||
      (variants.length === 1 && variants[0].title !== 'Default Title');

    var variantHtml = '';
    if (showVariants) {
      variantHtml = '<div class="bokbo-qv-variants">' +
        '<label for="bokbo-qv-variant">Option</label>' +
        '<select id="bokbo-qv-variant">';
      for (var vi = 0; vi < variants.length; vi++) {
        var v = variants[vi];
        var available = v.available !== false ? '' : ' (Sold out)';
        variantHtml += '<option value="' + v.id + '"' +
          (!v.available ? ' disabled' : '') +
          (vi === 0 ? ' selected' : '') + '>' +
          escapeHtml(v.title) + available +
          '</option>';
      }
      variantHtml += '</select></div>';
    }

    var firstVariant = variants.length ? variants[0] : null;
    var firstAvailable = firstVariant && firstVariant.available !== false;

    // Pull the dialog + signal from the live modal state. If qvState is
    // null the modal was closed between XHR send and render — bail.
    if (!qvState) return;
    var dialog = qvState.dialog;
    var signal = qvState.controller.signal;
    if (!dialog) return;

    dialog.innerHTML =
      '<button class="bokbo-qv-close" aria-label="Close">\u00d7</button>' +
      '<div class="bokbo-qv-body">' +
        (img ? '<div class="bokbo-qv-image"><img src="' + escapeHtml(img) + '" alt="' + escapeHtml(product.title || '') + '" loading="lazy"></div>' : '') +
        '<div class="bokbo-qv-info">' +
          '<h2 class="bokbo-qv-title">' + escapeHtml(product.title || '') + '</h2>' +
          (product.vendor ? '<div class="bokbo-qv-vendor">' + escapeHtml(product.vendor) + '</div>' : '') +
          (price ? '<div class="bokbo-qv-price">' + price + '</div>' : '') +
          (compareAt ? '<div class="bokbo-qv-compare">' + compareAt + '</div>' : '') +
          (desc ? '<div class="bokbo-qv-desc">' + desc + '</div>' : '') +
          variantHtml +
          '<button class="bokbo-qv-atc" id="bokbo-qv-atc"' +
            (!firstAvailable ? ' disabled' : '') + '>' +
            (firstAvailable ? 'Add to cart' : 'Sold out') +
          '</button>' +
          '<a class="bokbo-qv-link" href="' + escapeHtml(productUrl) + '">View full details &rarr;</a>' +
        '</div>' +
      '</div>';

    // All listeners from here on use { signal } so close() removes them
    // in one shot via controller.abort() — no per-listener teardown,
    // no risk of leaking a click handler onto the page after the dialog
    // DOM has been yanked.
    var closeBtn = dialog.querySelector('.bokbo-qv-close');
    if (closeBtn) closeBtn.addEventListener('click', bokboQuickViewClose, { signal: signal });

    // Variant change handler — update price and availability
    var variantSelect = document.getElementById('bokbo-qv-variant');
    var atcBtn = document.getElementById('bokbo-qv-atc');
    if (variantSelect) {
      variantSelect.addEventListener('change', function () {
        var selectedId = parseInt(variantSelect.value, 10);
        for (var si = 0; si < variants.length; si++) {
          if (variants[si].id === selectedId) {
            var sv = variants[si];
            var priceEl = dialog.querySelector('.bokbo-qv-price');
            if (priceEl) priceEl.textContent = formatCents(sv.price);
            var cmpEl = dialog.querySelector('.bokbo-qv-compare');
            if (cmpEl) {
              if (sv.compare_at_price && sv.compare_at_price > sv.price) {
                cmpEl.textContent = formatCents(sv.compare_at_price);
                cmpEl.style.display = '';
              } else {
                cmpEl.style.display = 'none';
              }
            }
            if (atcBtn) {
              atcBtn.disabled = !sv.available;
              atcBtn.textContent = sv.available ? 'Add to cart' : 'Sold out';
            }
            break;
          }
        }
      }, { signal: signal });
    }

    // Add to cart handler
    if (atcBtn) {
      atcBtn.addEventListener('click', function () {
        if (atcBtn.disabled) return;
        var variantId = variantSelect ? variantSelect.value : (firstVariant ? firstVariant.id : null);
        if (!variantId) return;
        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding\u2026';

        var cartXhr = new XMLHttpRequest();
        // Cancel the cart request if the modal closes mid-flight.
        signal.addEventListener('abort', function () {
          try { cartXhr.abort(); } catch (_) {}
        });
        cartXhr.open('POST', '/cart/add.js', true);
        cartXhr.setRequestHeader('Content-Type', 'application/json');
        cartXhr.onload = function () {
          if (cartXhr.status >= 200 && cartXhr.status < 300) {
            atcBtn.textContent = 'Added!';
            setTimeout(function () {
              bokboQuickViewClose();
              // Refresh cart count — trigger Shopify section rendering event
              document.dispatchEvent(new CustomEvent('cart:refresh'));
              // Also try updating the cart drawer / bubble if Dawn exposes it
              try {
                fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
                  var countEls = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
                  for (var ci = 0; ci < countEls.length; ci++) {
                    countEls[ci].textContent = cart.item_count;
                  }
                });
              } catch (_) {}
            }, 800);
          } else {
            atcBtn.disabled = false;
            atcBtn.textContent = 'Error — try again';
          }
        };
        cartXhr.onerror = function () {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Error — try again';
        };
        cartXhr.send(JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }));
      }, { signal: signal });
    }
  }

  function renderQuickViewError(msg) {
    if (!qvState) return;
    var dialog = qvState.dialog;
    var signal = qvState.controller.signal;
    if (!dialog) return;
    dialog.innerHTML =
      '<button class="bokbo-qv-close" aria-label="Close">\u00d7</button>' +
      '<div class="bokbo-qv-loading">' + escapeHtml(msg) + '</div>';
    var closeBtn = dialog.querySelector('.bokbo-qv-close');
    if (closeBtn) closeBtn.addEventListener('click', bokboQuickViewClose, { signal: signal });
  }

  function bokboQuickViewClose() {
    // Idempotent: if already closed, don't touch body.overflow or fire
    // controller.abort() twice — both are harmless but no-ops have a
    // clearer intent.
    if (!qvState) return;
    var s = qvState;
    qvState = null;

    // Single abort removes every listener wired with { signal } at
    // open-time: overlay click, close-btn click, document keydown,
    // variant change, ATC click, and the XHR abort wiring.
    try { s.controller.abort(); } catch (_) {}

    // Fully detach overlay + dialog from the DOM. The dialog is a child
    // of overlay, so removing overlay takes the dialog with it; we null
    // both explicitly to make the intent obvious in logs / debugging.
    if (s.overlay && s.overlay.parentNode) {
      s.overlay.parentNode.removeChild(s.overlay);
    }

    // Unlock body scroll. We never set pointer-events / z-index on
    // body or #MainContent, so nothing else to unwind.
    document.body.style.overflow = '';
  }

  // Quick view click delegation. Earlier version latched a single
  // module-level `qvListenerAttached` flag so the delegate was wired
  // exactly ONCE. That silently broke quick view after any code path
  // that called renderShell() a second time (re-search through the
  // in-page refine input, bokbo:navigate custom event, popstate
  // between pushState'd searches) — renderShell replaces the grid
  // DOM element, and the module-level flag kept the helper from
  // re-attaching to the new one. Result: clicks on the Quick view
  // button hit an element that never had a listener.
  //
  // Fix: track the CURRENTLY-wired grid element instead of a bool.
  // Re-attach whenever ensureQuickViewListener sees a grid that
  // isn't the one it last wired. Cheap — one addEventListener per
  // shell rebuild.
  var qvListenerAttachedTo = null;
  function ensureQuickViewListener() {
    if (!grid) return;
    if (qvListenerAttachedTo === grid) return;
    qvListenerAttachedTo = grid;
    grid.addEventListener('click', function (e) {
      // Diagnostic per Bug 1 — logs every click that reaches the
      // delegate so we can tell from the console whether the event
      // is even being delivered. Remove once quick view is verified
      // stable across all the re-render paths.
      // (click-detected log removed in cleanup pass — fired on every
      // grid click including non-quick-view targets.)
      var btn = e.target.closest('[data-bokbo-quick-view]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.getAttribute('data-bokbo-quick-view');
      var productUrl = btn.getAttribute('data-product-url') || '';
      if (!handle) return;

      // Find the clicked product's position in the live filtered
      // results array so the modal's swipe / arrow navigation can
      // page through the same set the shopper is currently browsing.
      // Match by handle — stable across re-sorts, cheap enough at
      // typical page sizes (< 500). Fallback to 0 if we can't locate
      // it (e.g. cards rendered from a stale result batch).
      var idx = -1;
      for (var i = 0; i < filteredResults.length; i++) {
        if (filteredResults[i] && filteredResults[i].handle === handle) {
          idx = i;
          break;
        }
      }
      if (idx < 0) idx = 0;

      bokboQuickViewOpen(handle, productUrl, filteredResults, idx);
    });
  }

  // Note: the module-level Escape-to-close listener that used to live
  // here was removed along with the singleton `qvOverlay` model. Each
  // bokboQuickViewOpen() now attaches its own document keydown listener
  // scoped to the open instance's AbortController signal, so the
  // listener is cleaned up atomically on close.

  // ── Init ────────────────────────────────────────────────────────

  function init() {
    injectStyles();

    container = renderContainer();

    // On the dedicated /pages/bokbo-search route, the Liquid template
    // paints a shimmer skeleton inside #bokbo-search-page-shell the
    // instant the page HTML arrives — no blank frame, no 404 flash.
    // Find that shell and replace it in place so the transition from
    // skeleton to real results is a single DOM swap. If we can't find
    // the shell (older theme deploy, custom embed) we fall back to the
    // legacy native-container replacement.
    var shell = isBokboSearchPage()
      ? document.getElementById('bokbo-search-page-shell')
      : null;
    if (shell && shell.parentNode) {
      shell.parentNode.replaceChild(container, shell);
      console.log('[BokBo Results] Replaced #bokbo-search-page-shell with BokBo container.');
    } else {
      var nativeContainer = findNativeContainer();
      if (nativeContainer) {
        nativeContainer.innerHTML = '';
        nativeContainer.appendChild(container);
        console.log('[BokBo Results] Replaced native container:', nativeContainer.tagName, nativeContainer.id || nativeContainer.className);
      } else {
        var footer = document.querySelector('footer') || document.querySelector('#shopify-section-footer');
        if (footer) {
          footer.parentNode.insertBefore(container, footer);
        } else {
          document.body.appendChild(container);
        }
        console.log('[BokBo Results] No native container found — injected standalone.');
      }
    }

    // Force-hide breadcrumbs via inline style — wins any specificity battle.
    // The CSS rules in STYLES and the anti-flash block cover most cases, but
    // some themes use inline styles or late-injected CSS that overrides them.
    // This JS pass is the nuclear option.
    var breadcrumbEls = document.querySelectorAll(
      'nav.breadcrumbs, .breadcrumbs__list, ol.breadcrumbs__list, ' +
      '[aria-label="Breadcrumb navigation"], .breadcrumb, nav.breadcrumb'
    );
    for (var bi = 0; bi < breadcrumbEls.length; bi++) {
      breadcrumbEls[bi].style.setProperty('display', 'none', 'important');
      breadcrumbEls[bi].style.setProperty('visibility', 'hidden', 'important');
      breadcrumbEls[bi].style.setProperty('height', '0', 'important');
      breadcrumbEls[bi].style.setProperty('overflow', 'hidden', 'important');
      breadcrumbEls[bi].style.setProperty('margin', '0', 'important');
      breadcrumbEls[bi].style.setProperty('padding', '0', 'important');
    }
    // (previously logged "Force-hid N breadcrumb element(s)" here —
    // removed in the log-cleanup pass; fires on every page load and
    // isn't actionable once the selector list is stable.)

    // Check if this is a direct brand search (from brand name click)
    var isBrandSearch = params.get('bokbo_brand') === '1';

    if (isBrandSearch) {
      // Immediately set brand filter — query IS the brand name
      activeFilters.brand = query;
      console.log('[BokBo Results] Brand search detected via URL param — brand:', query);
    }

    // ── Read filter params from URL ──────────────────────────────
    // When navigating from a brand click (or any URL with filter params),
    // pre-apply those filters so results are filtered on first load.
    var urlFilterKeys = ['category', 'product_type', 'subcategory', 'price', 'color', 'size_clothing', 'size_shoe', 'size_kids'];
    var hasUrlFilters = false;
    for (var fi = 0; fi < urlFilterKeys.length; fi++) {
      var fk = urlFilterKeys[fi];
      var fv = params.get(fk);
      if (fv) {
        activeFilters[fk] = fv;
        hasUrlFilters = true;
        console.log('[BokBo Results] URL filter:', fk, '=', fv);
      }
    }

    // ── Back-button instant-paint — DISABLED ───────────────────────
    //
    // The sessionStorage back-button cache was painting stale pre-fix
    // results (wrong size sort order), and the background-refresh
    // re-render path wasn't reliably overwriting the cache paint.
    // Net effect: shopper saw the wrong order for the entire tab
    // session even after the server-side sort was fixed.
    //
    // For now the page always does a fresh search on load. Slightly
    // slower on back-nav, but correct. Can be re-enabled by restoring
    // the original read+paint block below AND ensuring the background
    // refresh in this callback resets shownCount + clears the grid
    // before re-rendering (see the now-present reset code down in the
    // searchApi callback). Past incidents logged under v1/v2/v3 bumps.
    //
    // The removeItem sweep stays — older tabs and older widget builds
    // may have left stale entries behind, and purging them avoids
    // storage bloat if / when the cache gets re-enabled.
    var backCacheHit = false;
    try { sessionStorage.removeItem('bokbo_last_results'); } catch (_) {}
    try { sessionStorage.removeItem('bokbo_last_results_v2'); } catch (_) {}
    try { sessionStorage.removeItem(BACK_CACHE_KEY); } catch (_) {}

    // No cache-read / cache-paint — always fall through to the normal
    // loading → fetch flow below.
    renderLoading();

    // Build initial filters from URL params (brand, size, color, etc.)
    var initialFilters = (isBrandSearch || hasUrlFilters) ? buildApiFilters() : null;

    // Perf instrumentation (Fix 4): capture the full search-flow elapsed
    // time so we can see end-to-end where the 6-second widget lag lives.
    // Logged regardless of success / no-results / error path.
    var _searchStartedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    var _logTotal = function (label) {
      var now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      console.log('[BokBo] total render time:', Math.round(now - _searchStartedAt), 'ms', label ? '(' + label + ')' : '');
    };

    // Dedupe guard (Fix 4): the same init() path can get poked by a
    // bokbo:navigate or popstate event that fires during the settings
    // race window. initSearchFired ensures only the first searchApi
    // call runs; subsequent pokes for the same query no-op.
    if (window.__bokboInitSearchFired) {
      console.log('[BokBo Results] init() search already fired for this page — skipping duplicate');
      return;
    }
    window.__bokboInitSearchFired = true;

    // Fire the search — request up to 200 results, render in batches via scroll.
    // Back-button cache is currently disabled (see the block above),
    // so this is always the shopper's first paint — never a background
    // refresh over a pre-painted cached grid.
    searchApi(query, initialFilters, function (err, data) {
      clearTimeout(resultsStillSearchingTimer);
      resultsStillSearchingTimer = null;

      if (err) {
        console.error('[BokBo Results] API error:', err);
        renderError();
        _logTotal('error');
        return;
      }

      allResults = data && data.results ? data.results : [];
      console.log('[BokBo] STEP 1 — raw results from server, top 3:',
        allResults.slice(0, 3).map(function (r) {
          return r.title + ' sizes:' + JSON.stringify((r.available_sizes || []).slice(0, 4));
        }));
      updateServerHandledSizeFromMeta(data);
      console.log('[BokBo Results] Received', allResults.length, 'results',
        '| serverHandledSize:', serverHandledSize);

      // Diagnostic (Step 1) — log the order the API RETURNED, before any
      // client-side sort / filter runs. If these look right but the page
      // renders a different order, the problem is downstream (applyFilters,
      // cache paint-over, render batcher). If these look wrong, the
      // problem is the API itself or the request shape — compare against
      // what /search is returning in a direct curl with the same body.
      try {
        console.log('[BokBo] received results order:');
        (data && data.results ? data.results : []).slice(0, 5).forEach(function (r, i) {
          console.log('  ' + (i + 1) + '. ' + (r.title || '(no title)') +
            ' — sizes: ' + ((r.available_sizes || []).join(', ') || '(none)'));
        });
        console.log('[BokBo] applied_filters:', data && data.meta && data.meta.applied_filters);
        console.log('[BokBo] relaxed_filters:', data && data.meta && data.meta.relaxed_filters);
      } catch (_) { /* never let logging break the render path */ }

      if (allResults.length === 0) {
        if (!handleNoResultsReason(data)) renderNoResults();
        _logTotal('no results');
        return;
      }

      // Apply saved sort preference before first render
      applyFilters();

      // Build the page shell (header + filter bar + empty grid + footer)
      renderShell();

      // If this was a direct brand search (bokbo_brand=1 URL param), update UI to reflect
      // the active brand filter — dropdown, pills, clear button.
      if (isBrandSearch && activeFilters.brand) {
        var brandSel = document.getElementById('bokbo-filter-brand');
        if (brandSel) {
          var brandVal = activeFilters.brand;
          if (!brandSel.querySelector('option[value="' + CSS.escape(brandVal) + '"]')) {
            brandSel.innerHTML += '<option value="' + escapeHtml(brandVal) + '">' + escapeHtml(brandVal) + '</option>';
          }
          brandSel.value = brandVal;
          brandSel.classList.add('bokbo-filter-active');
        }
        updateFilterPills();
        var clearBtnBrand = document.getElementById('bokbo-filter-clear');
        if (clearBtnBrand) clearBtnBrand.disabled = false;
      }

      // If filters were pre-applied from URL params, sync the dropdown UI
      if (hasUrlFilters) {
        var allFilterKeys = ['category', 'product_type', 'subcategory', 'price', 'color', 'size_clothing', 'size_shoe', 'size_kids'];
        for (var uf = 0; uf < allFilterKeys.length; uf++) {
          var ufKey = allFilterKeys[uf];
          var ufVal = activeFilters[ufKey];
          if (!ufVal) continue;
          var ufSel = document.getElementById('bokbo-filter-' + ufKey);
          if (ufSel) {
            // For size dropdowns, the option value includes group prefix (e.g. 'shoe:11')
            // For others, it's the direct value
            if (ufSel.querySelector('option[value="' + CSS.escape(ufVal) + '"]')) {
              ufSel.value = ufVal;
              ufSel.classList.add('bokbo-filter-active');
            }
          }
        }
        updateFilterPills();
        var clearBtnUrl = document.getElementById('bokbo-filter-clear');
        if (clearBtnUrl) clearBtnUrl.disabled = false;

        // Show match count
        var matchLabelUrl = document.getElementById('bokbo-filter-match-count');
        if (matchLabelUrl) {
          matchLabelUrl.textContent = filteredResults.length + ' match' + (filteredResults.length !== 1 ? 'es' : '');
        }
      }

      // Check for color detection — when the query contained a color word
      // ("red dress", "blue jeans"), the server applied it as a hard filter
      // and echoes it back so we can pre-select the color pill. Only honor
      // this when the user hasn't already set a color filter via URL.
      if (data.detected_color && !activeFilters.color) {
        var detectedColor = data.detected_color;
        console.log('[BokBo Results] Color detected in query:', detectedColor, '— pre-selecting filter');
        activeFilters.color = detectedColor;

        var colorSel = document.getElementById('bokbo-filter-color');
        if (colorSel) {
          // The detected color may not appear as a dropdown option yet if the
          // result set is small — ensure the option exists before selecting.
          if (!colorSel.querySelector('option[value="' + CSS.escape(detectedColor) + '"]')) {
            colorSel.innerHTML += '<option value="' + escapeHtml(detectedColor) + '">' + escapeHtml(detectedColor) + '</option>';
          }
          colorSel.value = detectedColor;
          colorSel.classList.add('bokbo-filter-active');
        }
        updateFilterPills();
        var clearBtnColor = document.getElementById('bokbo-filter-clear');
        if (clearBtnColor) clearBtnColor.disabled = false;
      }

      // Same pattern for size — parse meta.applied_filters for "size: X"
      // and pre-select the matching size dropdown. This also lights up
      // the size pill purple on every literal-match card via isActiveSize.
      syncSizeFilterFromMeta(data);
      syncCategoryFilterFromMeta(data);

      // Check for brand detection — if the API detected a dominant brand,
      // auto-select it and fetch ALL products from that brand.
      if (data.detected_brand && !isBrandSearch) {
        console.log('[BokBo Results] Brand detected:', data.detected_brand, '— auto-selecting and fetching all brand products');
        activeFilters.brand = data.detected_brand;

        // Update the brand dropdown to reflect the auto-selection
        var brandSel = document.getElementById('bokbo-filter-brand');
        if (brandSel) {
          // Brand might not be in the dropdown yet if results are limited —
          // ensure the option exists
          if (!brandSel.querySelector('option[value="' + CSS.escape(data.detected_brand) + '"]')) {
            brandSel.innerHTML += '<option value="' + escapeHtml(data.detected_brand) + '">' + escapeHtml(data.detected_brand) + '</option>';
          }
          brandSel.value = data.detected_brand;
          brandSel.classList.add('bokbo-filter-active');
        }

        // Update pills and clear button
        updateFilterPills();
        var clearBtn = document.getElementById('bokbo-filter-clear');
        if (clearBtn) clearBtn.disabled = false;

        // Make a second API call with the brand filter to get ALL brand products
        var brandFilters = buildApiFilters();
        searchApi(query, brandFilters, function (err2, data2) {
          if (err2) {
            console.error('[BokBo Results] Brand follow-up API error:', err2);
            // Fall through — keep the initial results
            console.log('[BokBo] STEP 5 — about to render, top 3:',
              filteredResults.slice(0, 3).map(function (r) { return r.title; }));
            appendNextBatch();
            if (filteredResults.length > BOKBO.perPage) startScrollListener();
            return;
          }

          // Diagnostic — arrival order on the brand follow-up path.
          try {
            console.log('[BokBo] received results order (brand follow-up):');
            (data2 && data2.results ? data2.results : []).slice(0, 5).forEach(function (r, i) {
              console.log('  ' + (i + 1) + '. ' + (r.title || '(no title)') +
                ' — sizes: ' + ((r.available_sizes || []).join(', ') || '(none)'));
            });
            console.log('[BokBo] applied_filters:', data2 && data2.meta && data2.meta.applied_filters);
            console.log('[BokBo] relaxed_filters:', data2 && data2.meta && data2.meta.relaxed_filters);
          } catch (_) {}

          allResults = data2 && data2.results ? data2.results : [];
          console.log('[BokBo] STEP 1 — raw results from server, top 3:',
            allResults.slice(0, 3).map(function (r) {
              return r.title + ' sizes:' + JSON.stringify((r.available_sizes || []).slice(0, 4));
            }));
          updateServerHandledSizeFromMeta(data2);
          console.log('[BokBo Results] Brand follow-up received', allResults.length, 'results');

          applyFilters();
          rebuildFilterDropdowns();
          updateFilterPills();

          // Update match count
          var matchLabel = document.getElementById('bokbo-filter-match-count');
          if (matchLabel) {
            matchLabel.textContent = filteredResults.length + ' match' + (filteredResults.length !== 1 ? 'es' : '');
          }

          // Update header count and render
          grid.innerHTML = '';
          shownCount = 0;
          console.log('[BokBo] STEP 5 — about to render, top 3:',
            filteredResults.slice(0, 3).map(function (r) { return r.title; }));
          appendNextBatch();
          if (filteredResults.length > BOKBO.perPage) startScrollListener();
        });
        return; // don't render the initial batch — wait for the brand follow-up
      }

      // Defensive re-render reset. With the back-button cache currently
      // disabled these three calls are no-ops on the normal first-paint
      // path (shownCount is still 0, grid was just rebuilt by
      // renderShell, scroll listener was never started). They're
      // retained so that re-enabling the cache later can't regress
      // the "background refresh leaves shownCount stale" bug: any
      // prior paint of this grid in this tab gets cleanly reset here
      // before the fresh paint runs.
      grid.innerHTML = '';
      shownCount = 0;
      stopScrollListener();

      // Append the first batch immediately
      console.log('[BokBo] STEP 5 — about to render, top 3:',
        filteredResults.slice(0, 3).map(function (r) { return r.title; }));
      appendNextBatch();
      renderSizeRelaxationNotice(data);
      saveBackButtonCache();

      // Start observing scroll position for subsequent batches
      if (filteredResults.length > BOKBO.perPage) {
        startScrollListener();
      }

      _logTotal('first batch painted');
    });
  }

  // ── Brand click handler ─────────────────────────────────────────
  // Clicking a brand name on a card navigates to a new search for that brand.
  // Carries over any currently active filters (size, color, etc.) via URL params.
  // Uses event delegation on body since cards are dynamically added.
  //
  // The brand element now lives INSIDE the card's <a> (at the top of the
  // card body, where the breadcrumb used to sit), so we need BOTH
  // preventDefault (cancel the outer <a>'s navigation) and stopPropagation
  // (so no other card-level click handler picks the event up as a card
  // click). Same contract the bokbo-search-v3 widget's goToBokboSearch
  // helper uses for its intercepts — sessionStorage stash + /pages/bokbo-search.
  function handleBrandActivate(brandEl, e) {
    e.preventDefault();
    e.stopPropagation();

    var brandName = brandEl.getAttribute('data-brand');
    if (!brandName) return;
    brandNavigateToBrand(brandName);
  }

  document.body.addEventListener('click', function (e) {
    var brandEl = e.target.closest('.bokbo-card-brand');
    if (!brandEl) return;
    handleBrandActivate(brandEl, e);
  });

  // ── Heart button click delegation ──────────────────────────────
  // Toggles wishlist membership, pulses the heart, and shows a toast.
  // Stops propagation so the outer card <a> doesn't also fire (same
  // pattern as the brand element — both sit inside the card link and
  // both need to override its default navigation). Uses filteredResults
  // to rehydrate the full product record from just data-product-id,
  // so the wishlist entry has title / price / image / vendor even
  // when the card DOM only carries the identity attributes.
  document.body.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.bokbo-heart-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var productId = btn.getAttribute('data-product-id');
    if (!productId) return;

    // Prefer the live filteredResults record — it has the full payload
    // the sync endpoint wants. Fall back to a minimal record built from
    // data attributes when the click happens on a card whose backing
    // product has since been filtered out (rare but possible during a
    // concurrent filter change).
    var product = null;
    for (var i = 0; i < filteredResults.length; i++) {
      if (filteredResults[i] && filteredResults[i].shopify_id === productId) {
        product = filteredResults[i];
        break;
      }
    }
    if (!product) {
      product = {
        shopify_id: productId,
        handle: btn.getAttribute('data-handle') || '',
        title: btn.getAttribute('data-title') || '',
        price: 0,
        image_url: '',
        vendor: '',
      };
    }

    var added = toggleWishlist(product);
    btn.classList.add('bokbo-heart-pulse');
    setTimeout(function () { btn.classList.remove('bokbo-heart-pulse'); }, 300);
    showWishlistToast(added ? '\u2665 Saved to favorites' : 'Removed from favorites');
  });

  // Keyboard activation — the brand div has role="link" tabindex="0",
  // so Enter / Space should trigger the same navigation a click would.
  document.body.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var brandEl = e.target && e.target.classList && e.target.classList.contains('bokbo-card-brand')
      ? e.target
      : null;
    if (!brandEl) return;
    handleBrandActivate(brandEl, e);
  });

  function brandNavigateToBrand(brandName) {
    if (!brandName) return;

    // Build URL with brand flag + any active filters
    var searchParams = new URLSearchParams();
    searchParams.set('q', brandName);
    searchParams.set('bokbo_brand', '1');

    // Carry over active filters
    if (activeFilters.category)      searchParams.set('category', activeFilters.category);
    if (activeFilters.product_type)  searchParams.set('product_type', activeFilters.product_type);
    if (activeFilters.subcategory)   searchParams.set('subcategory', activeFilters.subcategory);
    if (activeFilters.price !== '')   searchParams.set('price', activeFilters.price);
    if (activeFilters.color)         searchParams.set('color', activeFilters.color);
    if (activeFilters.size_clothing) searchParams.set('size_clothing', activeFilters.size_clothing);
    if (activeFilters.size_shoe)     searchParams.set('size_shoe', activeFilters.size_shoe);
    if (activeFilters.size_kids)     searchParams.set('size_kids', activeFilters.size_kids);

    // Stash the brand name in sessionStorage too, matching the convention
    // bokbo-search-v3.js uses for its form / keydown / link intercepts, so
    // the destination page has a consistent source-of-truth for the query.
    try { window.sessionStorage.setItem('bokbo_query', brandName); } catch (_) {}

    window.location.href = '/pages/bokbo-search?' + searchParams.toString();
  }

  // ── Boot ────────────────────────────────────────────────────────
  function bootWithSettings() {
    // Set the CSS variable to the compile-time default immediately so
    // styles render correctly even before settings arrive.
    document.documentElement.style.setProperty('--bokbo-accent', BOKBO.accentColor);

    // Fast path: if the app proxy <script src="/apps/bokbo/settings.js">
    // already ran, window.__BOKBO_SETTINGS is set synchronously — no
    // fetch needed, no timeout race.
    var proxySettings = window.__BOKBO_SETTINGS || null;
    if (proxySettings) {
      console.log('[BokBo Results] Using pre-loaded settings from app proxy');
      // Cache it so fetchSettings also picks it up if called later
      window[SETTINGS_CACHE_KEY] = { data: proxySettings, fetchedAt: Date.now() };
    }

    var onSettings = function (settings) {
      applySettings(settings);
      if (!BOKBO.searchEnabled) {
        console.log('[BokBo Results] Search disabled via settings — leaving native Shopify results intact.');
        // Reveal the native results we hid during the anti-flash phase.
        removeAntiFlash();
        // Diagnostic: inspect what the native search DOM actually contains.
        // This tells us whether the "no results" message the user is seeing
        // is (a) Shopify's own empty state because native search legitimately
        // returned zero hits, or (b) a container that was hidden/removed by
        // BokBo earlier.
        try {
          var inspectSel = [
            '#shopify-section-template--search',
            '.template-search',
            '.search-results',
            '#search-results',
            '[data-section-type="search"]',
            'main#MainContent',
            'main[role="main"]'
          ];
          inspectSel.forEach(function (sel) {
            var el = document.querySelector(sel);
            if (!el) return;
            var cs = window.getComputedStyle(el);
            console.log('[BokBo Results] native DOM "' + sel + '":',
              'visibility=' + cs.visibility,
              'display=' + cs.display,
              'childCount=' + el.children.length,
              'textSample=' + (el.textContent || '').trim().slice(0, 120));
          });
        } catch (_) {}
        return;
      }
      init();
      // init() has replaced the native container's contents with the BokBo
      // results wrapper; the anti-flash style still applies to the outer
      // native container, so drop it now that BokBo is ready to render.
      removeAntiFlash();
    };

    if (proxySettings) {
      // Synchronous path — no fetch, no timeout, no race.
      onSettings(proxySettings);
    } else {
      // Fallback: stores that haven't added the proxy snippet yet.
      fetchSettings(onSettings);
    }
  }

  // Safety net: if settings never resolve (fetch + timeout both fail to fire
  // the callback for any reason), don't leave the page permanently blank.
  // Shorter timeout for proxy-enabled stores (settings are synchronous), but
  // keep a backstop for the XHR fallback path too.
  var safetyMs = window.__BOKBO_SETTINGS ? 1500 : 4000;
  setTimeout(function () {
    if (document.getElementById(ANTI_FLASH_STYLE_ID)) {
      console.warn('[BokBo Results] Settings never resolved within ' + safetyMs + 'ms — dropping anti-flash to avoid blank page.');
      removeAntiFlash();
    }
  }, safetyMs);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootWithSettings);
  } else {
    bootWithSettings();
  }

})();
